import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { Stage, Layer, Rect, Text, Image as KonvaImage, Group, Line, Circle, Ellipse, Transformer } from 'react-konva';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { 
  X, Plus, Type, Image as ImageIcon, LayoutGrid, Users, Trash2, Save,
  ZoomIn, ZoomOut, RotateCw, RotateCcw, Copy, AlignLeft, AlignCenter, AlignRight,
  AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd,
  AlignHorizontalJustifyCenter, Grid3x3,
  Move, Lock, Unlock, Eye, EyeOff, ChevronUp, ChevronDown,
  Download, Undo, Redo, Building2, Pencil, Ratio,
} from 'lucide-react';
import Konva from 'konva';
import jsPDF from 'jspdf';
import { showSuccess, showError, confirmAction } from '@/lib/swal';
import {
  doc,
  setDoc,
  getDoc,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { guestSeatMultilineLabel, splitGuestNameIntoSeatLines } from '@/lib/seatLabelUtils';
import {
  FLOOR_PLAN_DEFAULT_ROOM_KEY,
  buildRoomDesignsForFirestore,
  normalizeFloorPlanRoomKey,
} from '@/lib/floor-plan-room-keys';

// Helper function to remove undefined values from objects (Firestore doesn't support undefined)
function sanitizeForFirestore<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return null as any;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForFirestore(item)) as any;
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = sanitizeForFirestore(value);
      }
    }
    return cleaned;
  }
  
  return obj;
}

/** Floor plan guest list (surname variant): parse structured or free-text names. */
function parseGuestSurnameFirstname(guest: {
  firstName?: string;
  lastName?: string;
  name?: string;
}): { surname: string; firstname: string } {
  const first = (guest.firstName && String(guest.firstName).trim()) || '';
  const last = (guest.lastName && String(guest.lastName).trim()) || '';
  if (last || first) {
    return { surname: last || first || '?', firstname: last ? first : '' };
  }
  const raw = (typeof guest.name === 'string' && guest.name.trim()) || '';
  if (!raw) {
    return { surname: '?', firstname: '' };
  }
  if (raw.includes(',')) {
    const idx = raw.indexOf(',');
    return {
      surname: raw.slice(0, idx).trim() || '?',
      firstname: raw.slice(idx + 1).trim(),
    };
  }
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return {
      surname: parts[parts.length - 1] || '?',
      firstname: parts.slice(0, -1).join(' '),
    };
  }
  return { surname: parts[0] || '?', firstname: '' };
}

/** Guest name for display: first + last name, falling back to the older single `name` field. */
function guestDisplayName(guest: { firstName?: string; lastName?: string; name?: string } | null | undefined): string {
  if (!guest) return '';
  const structured = [guest.firstName, guest.lastName]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(' ');
  if (structured) return structured;
  return typeof guest.name === 'string' ? guest.name.trim() : '';
}

/**
 * Ordered table ids a guest list shows. Unset means every table in the room; an empty list means none
 * (the user removed them all). Ids from another room (e.g. the list was copied) fall back to every table.
 */
function resolveGuestListTableIds(
  element: { guestListSelectedTables?: string[] },
  roomTables: { id: string }[]
): string[] {
  const all = roomTables.map((t) => t.id);
  const saved = element.guestListSelectedTables;
  if (!saved) return all;
  const valid = new Set(all);
  const kept = saved.filter((id) => valid.has(id));
  return saved.length > 0 && kept.length === 0 ? all : kept;
}

function formatSurnameCommaFirstname(surname: string, firstname: string): string {
  if (firstname) {
    return `${surname}, ${firstname}`;
  }
  return surname;
}

/** Primary: surname (A-Z). Secondary: first name within the same surname (e.g. Smith, Alan before Smith, James). */
function compareSurnameThenFirstname(
  sa: string,
  fa: string,
  sb: string,
  fb: string
): number {
  const ns = (x: string) => x.trim().replace(/\s+/g, ' ');
  const aS = ns(sa);
  const bS = ns(sb);
  const aF = ns(fa);
  const bF = ns(fb);
  const c = aS.localeCompare(bS, 'en-GB', { sensitivity: 'base', numeric: true });
  if (c !== 0) {
    return c;
  }
  if (!aF && !bF) {
    return 0;
  }
  if (!aF) {
    return 1;
  }
  if (!bF) {
    return -1;
  }
  return aF.localeCompare(bF, 'en-GB', { sensitivity: 'base', numeric: true });
}

/** Group key for surname alphabetical blocks (A-Z, 0-9, or #). */
function surnameBlockLetter(surname: string): string {
  const t = surname.trim();
  if (!t) {
    return '#';
  }
  const ch = t[0];
  if (/[A-Za-z]/.test(ch)) {
    return ch.toUpperCase();
  }
  if (/[0-9]/.test(ch)) {
    return '0-9';
  }
  return '#';
}

interface PDFExportModalProps {
  onClose: () => void;
  guests: any[];
  tables: any[];
  /** Optional map of room id to tables for multi-room table layout elements. */
  allRoomTables?: Map<string, any[]>;
  rooms?: { id: string; name: string }[];
  event?: { name: string; date: Date };
  eventId?: string;
  unitId?: string;
  /** Optional: parent refresh after floor plan design is persisted (e.g. reload rooms / design plans). */
  onFloorPlansChanged?: () => void | Promise<void>;
}

type PaperSize = 'A1' | 'A2' | 'A3' | 'A4' | 'A5' | 'A6';
type Orientation = 'portrait' | 'landscape';
type FrameStyle = 'none' | 'simple' | 'elegant' | 'decorative' | 'masonic';

interface CanvasElement {
  id: string;
  type: 'text' | 'image' | 'unitLogo' | 'tablePlan' | 'guestList' | 'guestListSurname' | 'shape';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  opacity: number;
  locked: boolean;
  visible: boolean;
  name?: string;
  // Text properties
  content?: string;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline';
  textAlign?: 'left' | 'center' | 'right';
  textColor?: string;
  backgroundColor?: string;
  // Image properties
  imageUrl?: string;
  imageData?: HTMLImageElement | HTMLCanvasElement;
  /** Width / height of the source image; used to restore aspect ratio after resize. */
  nativeAspectRatio?: number;
  unitLogoUrl?: string;
  cropX?: number;
  cropY?: number;
  cropWidth?: number;
  cropHeight?: number;
  // Guest list properties
  guestListFontSize?: number;
  guestListFontFamily?: string;
  guestListFontWeight?: 'normal' | 'bold';
  guestListColumns?: {
    name: boolean;
    seat: boolean;
  };
  guestListDirection?: 'across' | 'down'; // Layout direction for tables
  /** Max tables per row (across) or per column (down) before wrapping; omit for a single row or column. */
  guestListWrapCount?: number;
  guestListSelectedTables?: string[]; // Table IDs to display
  guestListHiddenTables?: string[]; // Table IDs to hide
  guestListShowDividers?: boolean;
  /** Room whose tables this list uses; defaults to the designer room tab when unset. */
  guestListRoom?: string;
  /** Guest List (Surname): max number of vertical strips; each strip fills top-to-bottom within the element height before the next strip (default 1). */
  guestListSurnameColumnCount?: number;
  // Shape properties
  shapeType?: 'rectangle' | 'circle' | 'line';
  fillColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
  // Table plan specific (vector-based rendering)
  tablePlanRoom?: string;
  tablePlanScale?: number; // Zoom level for the table plan
  tablePlanX?: number; // Pan offset X
  tablePlanY?: number; // Pan offset Y
  /** When true, scale/pan are derived at render time so the whole plan fits the element bounds. */
  tablePlanFit?: boolean;
  /**
   * 'page': tablePlanScale is mm per planner unit and tablePlanX/Y are mm. Unset (older designs):
   * they are designer screen pixels, which is kept so saved layouts do not move.
   */
  tablePlanUnits?: 'page';
}

interface TablePlanView {
  scale: number;
  panX: number;
  panY: number;
}

/**
 * Scale/pan a table plan is drawn with, in layer pixels. `pxPerMm` converts page units to pixels.
 */
function resolveTablePlanView(
  element: CanvasElement,
  tables: any[],
  frameWidth: number,
  frameHeight: number,
  pxPerMm: number
): TablePlanView {
  if (element.tablePlanFit) {
    const fit = computeTablePlanFit(tables, frameWidth, frameHeight);
    if (fit) return fit;
  }
  const unit = element.tablePlanUnits === 'page' ? pxPerMm : 1;
  return {
    scale: (element.tablePlanScale || 1) * unit,
    panX: (element.tablePlanX || 0) * unit,
    panY: (element.tablePlanY || 0) * unit,
  };
}

/** Fraction of the element kept clear around a fitted table plan. */
const TABLE_PLAN_FIT_PADDING = 0.04;

/**
 * Scale and pan that fit every table (including rotation and chairs) inside a frame of the given
 * size, centred. Pan is relative to the table-only bounding box origin used by `renderTablePlan`.
 */
function computeTablePlanFit(tables: any[], frameWidth: number, frameHeight: number): TablePlanView | null {
  if (tables.length === 0 || frameWidth <= 0 || frameHeight <= 0) return null;

  let originX = Infinity, originY = Infinity;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const include = (x: number, y: number, r = 0) => {
    minX = Math.min(minX, x - r);
    minY = Math.min(minY, y - r);
    maxX = Math.max(maxX, x + r);
    maxY = Math.max(maxY, y + r);
  };

  tables.forEach((table) => {
    const halfW = table.width / 2;
    const halfH = table.height / 2;
    originX = Math.min(originX, table.x - halfW);
    originY = Math.min(originY, table.y - halfH);

    const rad = ((table.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const place = (lx: number, ly: number, r = 0) =>
      include(table.x + lx * cos - ly * sin, table.y + lx * sin + ly * cos, r);

    place(-halfW, -halfH);
    place(halfW, -halfH);
    place(halfW, halfH);
    place(-halfW, halfH);

    const seatRadius = table.seatSize ?? 28;
    (table.seating || []).forEach((seat: any) => {
      if (seat && seat.seatNumber !== undefined) place(seat.x, seat.y, seatRadius);
    });
  });

  const contentWidth = maxX - minX;
  const contentHeight = maxY - minY;
  if (!(contentWidth > 0) || !(contentHeight > 0)) return null;

  const usableWidth = frameWidth * (1 - TABLE_PLAN_FIT_PADDING * 2);
  const usableHeight = frameHeight * (1 - TABLE_PLAN_FIT_PADDING * 2);
  const scale = Math.min(usableWidth / contentWidth, usableHeight / contentHeight);

  return {
    scale,
    panX: (frameWidth - contentWidth * scale) / 2 - (minX - originX) * scale,
    panY: (frameHeight - contentHeight * scale) / 2 - (minY - originY) * scale,
  };
}

const ELEMENT_TYPE_LABELS: Record<CanvasElement['type'], string> = {
  text: 'Text',
  image: 'Image',
  unitLogo: 'Logo',
  tablePlan: 'Table Layout',
  guestList: 'Guest List (Table)',
  guestListSurname: 'Guest List (Surname)',
  shape: 'Shape',
};

const ELEMENT_BASE_NAMES: Record<CanvasElement['type'], string> = {
  text: 'Text',
  image: 'Image',
  unitLogo: 'Logo',
  tablePlan: 'Table Layout',
  guestList: 'Guest List (Table)',
  guestListSurname: 'Guest List (Surname)',
  shape: 'Shape',
};

function isRenderableImage(image: unknown): image is CanvasImageSource {
  if (!image) {
    return false;
  }

  if (typeof window !== 'undefined') {
    if (typeof window.Image !== 'undefined' && image instanceof window.Image) {
      return true;
    }
    if (typeof window.HTMLImageElement !== 'undefined' && image instanceof window.HTMLImageElement) {
      return true;
    }
    if (typeof window.HTMLCanvasElement !== 'undefined' && image instanceof window.HTMLCanvasElement) {
      return true;
    }
  }

  if (typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) {
    return true;
  }

  return false;
}

/** Firestore rejects documents over 1 MiB; leave headroom for field names and metadata. */
const MAX_FLOOR_PLAN_DOC_BYTES = 1_000_000;

function isInlineImageUrl(url: string | undefined): url is string {
  return typeof url === 'string' && url.startsWith('data:');
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl);
  return response.blob();
}

function imageExtensionForType(contentType: string): string {
  const subtype = contentType.split('/')[1]?.split(/[+;]/)[0]?.toLowerCase();
  if (!subtype) return 'img';
  return subtype === 'jpeg' ? 'jpg' : subtype;
}

/** Name of the tab used before any named design plan exists. */
const DEFAULT_LAYOUT_TAB_NAME = 'Layout';

/** Font size a guest list is drawn with when none is saved (new lists are created at 10). */
const DEFAULT_GUEST_LIST_FONT_SIZE = 12;

/** Table names and surname letters are drawn this much larger than the guest rows. */
const GUEST_LIST_TITLE_SCALE = 1.15;

const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

const NON_TEXT_INPUT_TYPES = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'color', 'file', 'image']);

/** Key events aimed at a form field or keyboard-driven widget, which must keep their normal behaviour. */
function isEditableKeyTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement) {
    // Checkboxes, colour pickers etc. don't use Delete/arrows/Ctrl shortcuts, so leave those to the canvas
    return !NON_TEXT_INPUT_TYPES.has(target.type);
  }
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) {
    return true;
  }
  if (target.isContentEditable) return true;
  return !!target.closest('[role="slider"], [role="combobox"], [role="listbox"], [role="option"], [role="menu"], [role="menuitem"]');
}

interface PageSetup {
  paperSize: PaperSize;
  orientation: Orientation;
}

interface HistoryState {
  elements: CanvasElement[];
  page: PageSetup;
  timestamp: number;
}

interface HistoryStore {
  entries: HistoryState[];
  index: number;
  /** Groups rapid edits to the same field into one undo step. */
  mergeKey?: string;
}

const MAX_HISTORY_ENTRIES = 50;
const HISTORY_MERGE_WINDOW_MS = 1000;

/**
 * Copy for the undo history. Elements are only ever replaced, never mutated, so a shallow copy is
 * enough, and it keeps loaded images (`imageData`) that a JSON round-trip would drop.
 */
function snapshotElements(elts: CanvasElement[]): CanvasElement[] {
  return elts.map((el) => ({ ...el }));
}

interface FloorPlanDesignPlan {
  id: string;
  name: string;
  createdAt: string;
}

const PAPER_SIZES: Record<PaperSize, { width: number; height: number }> = {
  A1: { width: 594, height: 841 },
  A2: { width: 420, height: 594 },
  A3: { width: 297, height: 420 },
  A4: { width: 210, height: 297 },
  A5: { width: 148, height: 210 },
  A6: { width: 105, height: 148 },
};

function getPageDimensions(paperSize: PaperSize, orientation: Orientation): { width: number; height: number } {
  const size = PAPER_SIZES[paperSize] ?? PAPER_SIZES.A4;
  return orientation === 'landscape' ? { width: size.height, height: size.width } : size;
}

const GRID_SIZE = 10;

/** Largest canvas (in pixels) every supported browser can render for export. */
const MAX_EXPORT_CANVAS_PIXELS = 16_000_000;

const MIN_DESIGNER_ZOOM = 0.1;
const MAX_DESIGNER_ZOOM = 5;
/** Multiplier per wheel notch or zoom button press. */
const DESIGNER_ZOOM_STEP = 1.1;

/** Max canvas edge (px) while editing; export uses a higher pixelRatio so PDFs stay print quality. */
const MAX_DESIGNER_PREVIEW_PX = 1100;

/** Designer preview: cap texture size so large uploads do not slow Konva. */
const MAX_IMAGE_TEXTURE_EDGE_PX = 2048;

interface FloorPlanRoomCanvasState {
  elements: CanvasElement[];
  zoom: number;
  panX: number;
  panY: number;
  paperSize: PaperSize;
  orientation: Orientation;
  frameStyle: FrameStyle;
}

async function hydrateCanvasElements(rawElements: CanvasElement[]): Promise<CanvasElement[]> {
  return Promise.all(
    rawElements.map(async (el: CanvasElement) => {
      if ((el.type === 'image' || el.type === 'unitLogo') && (el.imageUrl || el.unitLogoUrl)) {
        const sourceUrl = el.unitLogoUrl || el.imageUrl;
        if (!sourceUrl) {
          return el;
        }

        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        return new Promise<CanvasElement>((resolve) => {
          img.onload = () => {
            const nativeAspectRatio =
              el.nativeAspectRatio ??
              (img.naturalWidth > 0 && img.naturalHeight > 0
                ? img.naturalWidth / img.naturalHeight
                : undefined);
            resolve({
              ...el,
              imageData: img,
              imageUrl: sourceUrl,
              unitLogoUrl: el.type === 'unitLogo' ? sourceUrl : el.unitLogoUrl,
              nativeAspectRatio,
            });
          };
          img.onerror = () => {
            console.error('Failed to load image:', sourceUrl);
            resolve({
              ...el,
              imageUrl: sourceUrl,
              unitLogoUrl: el.type === 'unitLogo' ? sourceUrl : el.unitLogoUrl,
            });
          };
          img.src = sourceUrl;
        });
      }
      return el;
    })
  );
}

function cloneElementsShallowForRoomCache(elts: CanvasElement[]): CanvasElement[] {
  return elts.map((el) => ({ ...el }));
}

function downscaleImageForDesignerTexture(img: HTMLImageElement): CanvasImageSource {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (w <= 0 || h <= 0) {
    return img;
  }
  if (w <= MAX_IMAGE_TEXTURE_EDGE_PX && h <= MAX_IMAGE_TEXTURE_EDGE_PX) {
    return img;
  }
  const scale = MAX_IMAGE_TEXTURE_EDGE_PX / Math.max(w, h);
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return img;
  }
  ctx.drawImage(img, 0, 0, cw, ch);
  return canvas;
}

function getImageSourceDimensions(image: CanvasImageSource): { width: number; height: number } | null {
  if (typeof window !== 'undefined' && image instanceof window.HTMLImageElement) {
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (width > 0 && height > 0) {
      return { width, height };
    }
  }
  if (typeof window !== 'undefined' && image instanceof window.HTMLCanvasElement) {
    if (image.width > 0 && image.height > 0) {
      return { width: image.width, height: image.height };
    }
  }
  return null;
}

function getElementNativeAspectRatio(element: CanvasElement): number | null {
  if (element.nativeAspectRatio && element.nativeAspectRatio > 0) {
    return element.nativeAspectRatio;
  }
  if (element.imageData) {
    const dims = getImageSourceDimensions(element.imageData);
    if (dims) {
      return dims.width / dims.height;
    }
  }
  return null;
}

function computeImageDimensionsForCanvas(
  naturalWidth: number,
  naturalHeight: number,
  canvasWidth: number,
  canvasHeight: number,
): { width: number; height: number; nativeAspectRatio: number } {
  const nativeAspectRatio = naturalWidth / naturalHeight;
  const maxWidth = (canvasWidth / 2) * 0.4;
  const maxHeight = (canvasHeight / 2) * 0.4;
  let width: number;
  let height: number;
  if (nativeAspectRatio >= maxWidth / maxHeight) {
    width = maxWidth;
    height = width / nativeAspectRatio;
  } else {
    height = maxHeight;
    width = height * nativeAspectRatio;
  }
  return { width, height, nativeAspectRatio };
}

export default function PDFExportModal({
  onClose,
  guests,
  tables,
  allRoomTables,
  rooms = [],
  event,
  eventId,
  unitId,
  onFloorPlansChanged,
}: PDFExportModalProps) {
  const [paperSize, setPaperSize] = useState<PaperSize>('A4');
  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const [frameStyle, setFrameStyle] = useState<FrameStyle>('none');
  const [elements, setElements] = useState<CanvasElement[]>([]);
  const [selectedElementIds, setSelectedElementIds] = useState<string[]>([]);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  /** Undo history lives in a ref so edits never build on a stale copy; the state mirrors it for the buttons. */
  const historyRef = useRef<HistoryStore>({ entries: [], index: -1 });
  const [historyPosition, setHistoryPosition] = useState({ index: -1, length: 0 });
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; elements: CanvasElement[] } | null>(null);
  const selectedElements = useMemo(
    () => elements.filter(el => selectedElementIds.includes(el.id)),
    [elements, selectedElementIds]
  );
  const allSelectedLocked = selectedElements.length > 0 && selectedElements.every(el => el.locked);
  const anySelectedLocked = selectedElements.some(el => el.locked);
  
  const previewStageRef = useRef<Konva.Stage>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [previewDimensions, setPreviewDimensions] = useState({ width: 800, height: 600 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved' | 'error'>('saved');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [unitLogoUrl, setUnitLogoUrl] = useState<string | null>(null);
  const [unitLogoImage, setUnitLogoImage] = useState<HTMLImageElement | null>(null);

  const [showCreateDesignModal, setShowCreateDesignModal] = useState(false);
  const [newDesignName, setNewDesignName] = useState('');
  const [renameDesignTarget, setRenameDesignTarget] = useState<{ id: string; name: string } | null>(
    null
  );
  const pendingSwitchToRoomIdRef = useRef<string | null>(null);
  const [designPlans, setDesignPlans] = useState<FloorPlanDesignPlan[]>([]);

  const floorPlanRoomCacheRef = useRef<Record<string, FloorPlanRoomCanvasState>>({});
  const [activeFloorPlanRoomId, setActiveFloorPlanRoomId] = useState<string | null>(null);
  const elementsRef = useRef(elements);
  const zoomRef = useRef(zoom);
  const panXRef = useRef(panX);
  const panYRef = useRef(panY);
  const paperSizeRef = useRef<PaperSize>(paperSize);
  const orientationRef = useRef<Orientation>(orientation);
  const frameStyleRef = useRef<FrameStyle>(frameStyle);
  const activeFloorPlanRoomIdRef = useRef<string | null>(null);
  /** True while a debounced auto-save is waiting to run. */
  const hasPendingSaveRef = useRef(false);

  const commitHistory = useCallback((next: HistoryStore) => {
    historyRef.current = next;
    setHistoryPosition({ index: next.index, length: next.entries.length });
  }, []);

  const currentPageSetup = useCallback(
    (): PageSetup => ({ paperSize: paperSizeRef.current, orientation: orientationRef.current }),
    []
  );

  /** Start a fresh history (loading a design or switching tabs). */
  const resetHistory = useCallback(
    (elts: CanvasElement[], page?: PageSetup) => {
      commitHistory({
        entries: [{ elements: snapshotElements(elts), page: page ?? currentPageSetup(), timestamp: Date.now() }],
        index: 0,
      });
    },
    [commitHistory, currentPageSetup]
  );

  const restoreHistoryEntry = useCallback((entry: HistoryState) => {
    setElements(snapshotElements(entry.elements));
    setPaperSize(entry.page.paperSize);
    setOrientation(entry.page.orientation);
  }, []);

  /**
   * Record a new state. Edits sharing a `mergeKey` within {@link HISTORY_MERGE_WINDOW_MS} (typing in a
   * field, dragging a slider) replace the previous entry, so one undo reverts the whole edit.
   */
  const saveToHistory = useCallback(
    (newElements: CanvasElement[], mergeKey?: string, page?: PageSetup) => {
      const current = historyRef.current;
      const now = Date.now();
      const entry: HistoryState = {
        elements: snapshotElements(newElements),
        page: page ?? currentPageSetup(),
        timestamp: now,
      };
      const atLatest = current.index === current.entries.length - 1;

      if (
        mergeKey &&
        atLatest &&
        current.index > 0 &&
        current.mergeKey === mergeKey &&
        now - (current.entries[current.index]?.timestamp ?? 0) < HISTORY_MERGE_WINDOW_MS
      ) {
        const entries = [...current.entries];
        entries[current.index] = entry;
        commitHistory({ entries, index: current.index, mergeKey });
        return;
      }

      const entries = current.entries.slice(0, current.index + 1);
      entries.push(entry);
      // Keep only the last 50 states
      if (entries.length > MAX_HISTORY_ENTRIES) {
        entries.shift();
      }
      commitHistory({ entries, index: entries.length - 1, mergeKey });
    },
    [commitHistory, currentPageSetup]
  );

  const undo = useCallback(() => {
    const current = historyRef.current;
    if (current.index <= 0) return;
    const index = current.index - 1;
    commitHistory({ entries: current.entries, index });
    restoreHistoryEntry(current.entries[index]);
  }, [commitHistory, restoreHistoryEntry]);

  const redo = useCallback(() => {
    const current = historyRef.current;
    if (current.index >= current.entries.length - 1) return;
    const index = current.index + 1;
    commitHistory({ entries: current.entries, index });
    restoreHistoryEntry(current.entries[index]);
  }, [commitHistory, restoreHistoryEntry]);

  useEffect(() => {
    elementsRef.current = elements;
  }, [elements]);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  useEffect(() => {
    panXRef.current = panX;
  }, [panX]);
  useEffect(() => {
    panYRef.current = panY;
  }, [panY]);
  useEffect(() => {
    paperSizeRef.current = paperSize;
  }, [paperSize]);
  useEffect(() => {
    orientationRef.current = orientation;
  }, [orientation]);
  useEffect(() => {
    frameStyleRef.current = frameStyle;
  }, [frameStyle]);
  useEffect(() => {
    activeFloorPlanRoomIdRef.current = activeFloorPlanRoomId;
  }, [activeFloorPlanRoomId]);

  const floorPlanTabRoomIds = useMemo(
    () => (designPlans.length > 0 ? designPlans.map((d) => d.id) : [FLOOR_PLAN_DEFAULT_ROOM_KEY]),
    [designPlans]
  );

  const floorPlanRoomTabLabel = useCallback(
    (id: string) => {
      const plan = designPlans.find((d) => d.id === id);
      if (plan) {
        return plan.name;
      }
      return id === FLOOR_PLAN_DEFAULT_ROOM_KEY ? DEFAULT_LAYOUT_TAB_NAME : id;
    },
    [designPlans]
  );

  const resolveTablesForDesignerRoom = useCallback(
    (roomId: string | null | undefined) => {
      const id =
        roomId && roomId !== FLOOR_PLAN_DEFAULT_ROOM_KEY ? roomId : undefined;
      if (id && allRoomTables instanceof Map) {
        const rt = allRoomTables.get(id);
        if (rt && Array.isArray(rt)) {
          return rt;
        }
      }
      return tables;
    },
    [tables, allRoomTables]
  );

  const isEventRoomId = useCallback(
    (id: string | null | undefined): id is string => !!id && rooms.some((room) => room.id === id),
    [rooms]
  );

  /**
   * Default event room for new table layouts and guest lists. Older designs keyed their tabs by room
   * id, so such a tab keeps its room; named design plans (`design-…`) are not rooms and use the first room.
   */
  const effectiveDesignerTabRoomId = useMemo(() => {
    if (isEventRoomId(activeFloorPlanRoomId)) {
      return activeFloorPlanRoomId;
    }
    return rooms[0]?.id;
  }, [activeFloorPlanRoomId, rooms, isEventRoomId]);

  /** Room an element draws from. Unknown ids (e.g. a design tab id saved by older versions) use the default room. */
  const resolveElementRoomId = useCallback(
    (explicitRoomId: string | null | undefined): string | undefined => {
      if (isEventRoomId(explicitRoomId)) {
        return explicitRoomId;
      }
      if (rooms.length === 0 && explicitRoomId && explicitRoomId !== FLOOR_PLAN_DEFAULT_ROOM_KEY) {
        // Room list not provided: still try the id against the per-room tables
        return explicitRoomId;
      }
      return effectiveDesignerTabRoomId;
    },
    [isEventRoomId, rooms.length, effectiveDesignerTabRoomId]
  );

  const getTablesForPlanOrGuestElement = useCallback(
    (explicitRoomId: string | null | undefined) => {
      const rid = resolveElementRoomId(explicitRoomId);
      return rid ? resolveTablesForDesignerRoom(rid) : tables;
    },
    [resolveElementRoomId, resolveTablesForDesignerRoom, tables]
  );

  const switchFloorPlanRoom = useCallback((newId: string) => {
    const currentRoomId = activeFloorPlanRoomIdRef.current;
    if (newId === currentRoomId) {
      return;
    }
    if (currentRoomId) {
      floorPlanRoomCacheRef.current[currentRoomId] = {
        elements: cloneElementsShallowForRoomCache(elementsRef.current),
        zoom: zoomRef.current,
        panX: panXRef.current,
        panY: panYRef.current,
        paperSize: paperSizeRef.current,
        orientation: orientationRef.current,
        frameStyle: frameStyleRef.current,
      };
    }
    const incoming =
      floorPlanRoomCacheRef.current[newId] ?? {
        elements: [],
        zoom: 1,
        panX: 0,
        panY: 0,
        paperSize: 'A4',
        orientation: 'portrait',
        frameStyle: 'none',
      };
    setActiveFloorPlanRoomId(newId);
    setElements(incoming.elements);
    setZoom(incoming.zoom);
    setPanX(incoming.panX);
    setPanY(incoming.panY);
    setPaperSize(incoming.paperSize);
    setOrientation(incoming.orientation);
    setFrameStyle(incoming.frameStyle);
    setSelectedElementIds([]);
    resetHistory(incoming.elements, { paperSize: incoming.paperSize, orientation: incoming.orientation });
  }, [resetHistory]);

  const createFloorPlanDesign = useCallback(async () => {
    const name = newDesignName.trim();
    if (!name) return;
    try {
      const newId = `design-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const now = Date.now();
      const createdAt = new Date(now).toISOString();
      const designPlan: FloorPlanDesignPlan = {
        id: newId,
        name,
        createdAt,
      };
      // Once named plans exist only they get tabs, so keep any work on the default Layout tab as a plan of its own
      const defaultLayoutElements =
        activeFloorPlanRoomIdRef.current === FLOOR_PLAN_DEFAULT_ROOM_KEY
          ? elementsRef.current
          : floorPlanRoomCacheRef.current[FLOOR_PLAN_DEFAULT_ROOM_KEY]?.elements ?? [];
      setDesignPlans((prev) => {
        const keepDefaultLayout =
          defaultLayoutElements.length > 0 && !prev.some((d) => d.id === FLOOR_PLAN_DEFAULT_ROOM_KEY);
        const layoutPlan: FloorPlanDesignPlan[] = keepDefaultLayout
          ? [{ id: FLOOR_PLAN_DEFAULT_ROOM_KEY, name: DEFAULT_LAYOUT_TAB_NAME, createdAt: new Date(now - 1).toISOString() }]
          : [];
        return [...layoutPlan, ...prev, designPlan].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      });
      floorPlanRoomCacheRef.current[newId] = {
        elements: [],
        zoom: 1,
        panX: 0,
        panY: 0,
        paperSize: 'A4',
        orientation: 'portrait',
        frameStyle: 'none',
      };
      pendingSwitchToRoomIdRef.current = newId;
      setNewDesignName('');
      setShowCreateDesignModal(false);
      showSuccess('Floor plan created');
    } catch (error) {
      console.error('Error creating floor plan:', error);
      showError('Failed to create floor plan');
    }
  }, [newDesignName]);

  useEffect(() => {
    const pending = pendingSwitchToRoomIdRef.current;
    if (!pending) {
      return;
    }
    if (designPlans.some((d) => d.id === pending)) {
      pendingSwitchToRoomIdRef.current = null;
      switchFloorPlanRoom(pending);
    }
  }, [designPlans, switchFloorPlanRoom]);

  const renameFloorPlanDesign = useCallback(async () => {
    if (!renameDesignTarget?.name.trim()) return;
    const name = renameDesignTarget.name.trim();
    try {
      setDesignPlans((prev) =>
        prev.map((plan) => (plan.id === renameDesignTarget.id ? { ...plan, name } : plan))
      );
      setRenameDesignTarget(null);
      showSuccess('Floor plan renamed');
    } catch (error) {
      console.error('Error renaming floor plan:', error);
      showError('Failed to rename floor plan');
    }
  }, [renameDesignTarget]);

  const deleteFloorPlanDesign = useCallback(
    async (roomId: string) => {
      const design = designPlans.find((d) => d.id === roomId);
      if (!design) {
        return;
      }
      const label = design?.name ?? 'this floor plan';
      const result = await confirmAction({
        title: 'Remove floor plan?',
        text: `Remove "${label}"? Design elements for this plan will be removed.`,
        confirmButtonText: 'Remove',
        icon: 'warning',
      });
      if (!result.isConfirmed) return;

      const remaining = designPlans.filter((d) => d.id !== roomId);
      try {
        if (activeFloorPlanRoomId === roomId && remaining.length > 0) {
          switchFloorPlanRoom(remaining[0].id);
        } else if (activeFloorPlanRoomId === roomId && remaining.length === 0) {
          switchFloorPlanRoom(FLOOR_PLAN_DEFAULT_ROOM_KEY);
        }

        delete floorPlanRoomCacheRef.current[roomId];
        if (roomId === FLOOR_PLAN_DEFAULT_ROOM_KEY && activeFloorPlanRoomId === roomId && remaining.length === 0) {
          // Removed the only plan, which was the Layout tab itself: it comes back empty
          setElements([]);
          setSelectedElementIds([]);
          resetHistory([]);
        }
        setDesignPlans(remaining);

        showSuccess('Floor plan removed');
      } catch (error) {
        console.error('Error removing floor plan:', error);
        showError('Failed to remove floor plan');
      }
    },
    [designPlans, activeFloorPlanRoomId, switchFloorPlanRoom, resetHistory]
  );

  /**
   * Stores a design image in Cloud Storage and returns its download URL. Images must not be kept
   * inline: the whole design lives in one Firestore document, which is capped at 1 MiB.
   */
  const uploadDesignImage = useCallback(
    async (blob: Blob): Promise<string> => {
      if (!unitId || !eventId) {
        throw new Error('Cannot upload images without an event');
      }
      const contentType = blob.type || 'image/png';
      const fileName = `floorplan-image-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${imageExtensionForType(contentType)}`;
      const fileRef = storageRef(storage, `units/${unitId}/events/${eventId}/${fileName}`);
      await uploadBytes(fileRef, blob, { contentType });
      return getDownloadURL(fileRef);
    },
    [unitId, eventId]
  );

  /** One upload per inline image, shared by every element (any tab, undo history, paste) that uses it. */
  const inlineImageUploadsRef = useRef(new Map<string, Promise<string | null>>());
  /** Bumped when a background change touches only other tabs, so auto-save still writes it. */
  const [roomCacheRevision, setRoomCacheRevision] = useState(0);

  // Auto-save function
  const autoSaveFloorPlan = useCallback(async () => {
    if (!unitId || !eventId) {
      return;
    }

    hasPendingSaveRef.current = false;
    setSaveStatus('saving');

    try {
      const stripImageData = (elts: CanvasElement[]) =>
        elts.map((el) => {
          const { imageData: _imageData, ...rest } = el;
          return rest;
        });

      const currentRoomId = activeFloorPlanRoomIdRef.current;
      const roomDesigns = buildRoomDesignsForFirestore(
        floorPlanRoomCacheRef.current,
        currentRoomId,
        {
          elements,
          zoom,
          panX,
          panY,
          paperSize,
          orientation,
          frameStyle,
        },
        stripImageData
      );

      const floorPlanData = {
        floorPlanSchemaVersion: 2,
        designPlans,
        roomDesigns,
        activeFloorPlanRoomId: currentRoomId,
        showGrid,
        snapToGrid,
        lastModified: new Date().toISOString(),
      };

      const sanitizedData = sanitizeForFirestore(floorPlanData);

      const approxBytes = new Blob([JSON.stringify(sanitizedData)]).size;
      if (approxBytes > MAX_FLOOR_PLAN_DOC_BYTES) {
        throw new Error(
          `The design is too large to save (${Math.round(approxBytes / 1024)} KB of a ${Math.round(MAX_FLOOR_PLAN_DOC_BYTES / 1024)} KB limit). Remove some elements or images.`
        );
      }

      await setDoc(
        doc(db, 'units', unitId, 'events', eventId, 'floorPlan', 'design'),
        sanitizedData
      );

      console.log('✅ Floor plan auto-saved');
      setSaveError(null);
      setSaveStatus('saved');
      if (onFloorPlansChanged) {
        void Promise.resolve(onFloorPlansChanged()).catch(() => {
          /* parent refresh is best-effort */
        });
      }
    } catch (error) {
      console.error('❌ Failed to auto-save floor plan:', error);
      setSaveError(error instanceof Error ? error.message : 'Unknown error');
      setSaveStatus('error');
      throw error;
    }
  }, [
    unitId,
    eventId,
    designPlans,
    paperSize,
    orientation,
    frameStyle,
    elements,
    zoom,
    panX,
    panY,
    showGrid,
    snapToGrid,
    onFloorPlansChanged,
  ]);

  // Load saved floor plan
  const loadFloorPlan = useCallback(async () => {
    if (!unitId || !eventId) {
      setIsLoading(false);
      return;
    }

    const ensureCacheTabs = (ids: string[]) => {
      for (const id of ids) {
        if (!floorPlanRoomCacheRef.current[id]) {
          floorPlanRoomCacheRef.current[id] = {
            elements: [],
            zoom: 1,
            panX: 0,
            panY: 0,
            paperSize: 'A4',
            orientation: 'portrait',
            frameStyle: 'none',
          };
        }
      }
    };

    try {
      const floorPlanDoc = await getDoc(
        doc(db, 'units', unitId, 'events', eventId, 'floorPlan', 'design')
      );

      if (floorPlanDoc.exists()) {
        const data = floorPlanDoc.data();
        console.log('📥 Loading saved floor plan design');

        const legacyPaperSize: PaperSize | undefined = data.paperSize;
        const legacyOrientation: Orientation | undefined = data.orientation;
        const legacyFrameStyle: FrameStyle | undefined = data.frameStyle;
        if (typeof data.showGrid === 'boolean') setShowGrid(data.showGrid);
        if (typeof data.snapToGrid === 'boolean') setSnapToGrid(data.snapToGrid);

        const savedDesignPlans: FloorPlanDesignPlan[] = Array.isArray(data.designPlans)
          ? data.designPlans
              .filter((plan: any) => plan && typeof plan.id === 'string' && typeof plan.name === 'string')
              .map((plan: any) => ({
                id: normalizeFloorPlanRoomKey(plan.id),
                name: plan.name,
                createdAt:
                  typeof plan.createdAt === 'string' ? plan.createdAt : new Date().toISOString(),
              }))
          : [];

        let roomDesignsRaw: Record<string, any> | null = null;
        if (
          data.roomDesigns &&
          typeof data.roomDesigns === 'object' &&
          !Array.isArray(data.roomDesigns)
        ) {
          roomDesignsRaw = data.roomDesigns as Record<string, any>;
        } else if (data.elements !== undefined) {
          roomDesignsRaw = {
            [FLOOR_PLAN_DEFAULT_ROOM_KEY]: {
              elements: Array.isArray(data.elements) ? data.elements : [],
              zoom: typeof data.zoom === 'number' ? data.zoom : 1,
              panX: typeof data.panX === 'number' ? data.panX : 0,
              panY: typeof data.panY === 'number' ? data.panY : 0,
            },
          };
        }

        floorPlanRoomCacheRef.current = {};

        if (roomDesignsRaw) {
          for (const [roomId, rd] of Object.entries(roomDesignsRaw)) {
            if (!rd || typeof rd !== 'object') continue;
            const normalizedRoomId = normalizeFloorPlanRoomKey(roomId);
            const els = Array.isArray(rd.elements) ? rd.elements : [];
            const hydrated = await hydrateCanvasElements(els as CanvasElement[]);
            floorPlanRoomCacheRef.current[normalizedRoomId] = {
              elements: hydrated,
              zoom: typeof rd.zoom === 'number' ? rd.zoom : 1,
              panX: typeof rd.panX === 'number' ? rd.panX : 0,
              panY: typeof rd.panY === 'number' ? rd.panY : 0,
              paperSize:
                typeof rd.paperSize === 'string'
                  ? (rd.paperSize as PaperSize)
                  : legacyPaperSize ?? 'A4',
              orientation:
                typeof rd.orientation === 'string'
                  ? (rd.orientation as Orientation)
                  : legacyOrientation ?? 'portrait',
              frameStyle:
                typeof rd.frameStyle === 'string'
                  ? (rd.frameStyle as FrameStyle)
                  : legacyFrameStyle ?? 'none',
            };
          }
        }
        let nextDesignPlans = savedDesignPlans;
        // Earlier versions hid the default Layout tab once a named plan was added; bring its work back
        const hiddenDefaultLayout =
          nextDesignPlans.length > 0 &&
          !nextDesignPlans.some((d) => d.id === FLOOR_PLAN_DEFAULT_ROOM_KEY) &&
          (floorPlanRoomCacheRef.current[FLOOR_PLAN_DEFAULT_ROOM_KEY]?.elements.length ?? 0) > 0;
        if (hiddenDefaultLayout) {
          const earliest = nextDesignPlans.reduce(
            (min, d) => (d.createdAt < min ? d.createdAt : min),
            nextDesignPlans[0].createdAt
          );
          const before = new Date(Date.parse(earliest) - 1);
          nextDesignPlans = [
            {
              id: FLOOR_PLAN_DEFAULT_ROOM_KEY,
              name: DEFAULT_LAYOUT_TAB_NAME,
              createdAt: Number.isNaN(before.getTime()) ? new Date(0).toISOString() : before.toISOString(),
            },
            ...nextDesignPlans,
          ];
        }
        if (nextDesignPlans.length === 0 && roomDesignsRaw) {
          nextDesignPlans = Object.keys(roomDesignsRaw).map((id, index) => ({
            id: normalizeFloorPlanRoomKey(id),
            name: `Design ${index + 1}`,
            createdAt: new Date().toISOString(),
          }));
        }
        setDesignPlans(nextDesignPlans);

        const tabIds =
          nextDesignPlans.length > 0
            ? nextDesignPlans.map((d) => d.id)
            : [FLOOR_PLAN_DEFAULT_ROOM_KEY];
        ensureCacheTabs(tabIds);

        let activeId =
          typeof data.activeFloorPlanRoomId === 'string'
            ? normalizeFloorPlanRoomKey(data.activeFloorPlanRoomId)
            : tabIds[0];
        if (!tabIds.includes(activeId)) {
          activeId = tabIds[0];
        }

        const activeState = floorPlanRoomCacheRef.current[activeId] ?? {
          elements: [],
          zoom: 1,
          panX: 0,
          panY: 0,
          paperSize: legacyPaperSize ?? 'A4',
          orientation: legacyOrientation ?? 'portrait',
          frameStyle: legacyFrameStyle ?? 'none',
        };

        setActiveFloorPlanRoomId(activeId);
        setElements(activeState.elements);
        setZoom(activeState.zoom);
        setPanX(activeState.panX);
        setPanY(activeState.panY);
        setPaperSize(activeState.paperSize);
        setOrientation(activeState.orientation);
        setFrameStyle(activeState.frameStyle);
        resetHistory(activeState.elements, {
          paperSize: activeState.paperSize,
          orientation: activeState.orientation,
        });

        console.log('✅ Floor plan design loaded successfully');
      } else {
        console.log('ℹ️ No saved floor plan design found');
        setDesignPlans([]);
        floorPlanRoomCacheRef.current = {};
        const tabIds = [FLOOR_PLAN_DEFAULT_ROOM_KEY];
        ensureCacheTabs(tabIds);
        const first = tabIds[0];
        const firstState = floorPlanRoomCacheRef.current[first];
        setActiveFloorPlanRoomId(first);
        setElements(firstState.elements);
        setZoom(firstState.zoom);
        setPanX(firstState.panX);
        setPanY(firstState.panY);
        setPaperSize(firstState.paperSize);
        setOrientation(firstState.orientation);
        setFrameStyle(firstState.frameStyle);
        resetHistory([], { paperSize: firstState.paperSize, orientation: firstState.orientation });
      }
    } catch (error) {
      console.error('❌ Failed to load floor plan:', error);
      setDesignPlans([]);
      floorPlanRoomCacheRef.current = {};
      const tabIds = [FLOOR_PLAN_DEFAULT_ROOM_KEY];
      for (const id of tabIds) {
        floorPlanRoomCacheRef.current[id] = {
          elements: [],
          zoom: 1,
          panX: 0,
          panY: 0,
          paperSize: 'A4',
          orientation: 'portrait',
          frameStyle: 'none',
        };
      }
      const first = tabIds[0];
      setActiveFloorPlanRoomId(first);
      setElements([]);
      setZoom(1);
      setPanX(0);
      setPanY(0);
      setPaperSize('A4');
      setOrientation('portrait');
      setFrameStyle('none');
      resetHistory([], { paperSize: 'A4', orientation: 'portrait' });
    } finally {
      setIsLoading(false);
    }
  }, [unitId, eventId, resetHistory]);

  useEffect(() => {
    if (!unitId) {
      setUnitLogoUrl(null);
      return;
    }

    const fetchUnitBranding = async () => {
      try {
        const unitDoc = await getDoc(doc(db, 'units', unitId));
        if (unitDoc.exists()) {
          const data = unitDoc.data() as { settings?: { logoUrl?: string | null } };
          const logo = data?.settings?.logoUrl;
          if (typeof logo === 'string' && logo.trim().length > 0) {
            setUnitLogoUrl(logo);
          } else {
            setUnitLogoUrl(null);
          }
        } else {
          setUnitLogoUrl(null);
        }
      } catch (error) {
        console.error('Failed to fetch unit branding:', error);
        setUnitLogoUrl(null);
      }
    };

    void fetchUnitBranding();
  }, [unitId]);

  // Event name for the exported file name when the parent does not pass the event
  const [loadedEventName, setLoadedEventName] = useState<string | null>(null);
  useEffect(() => {
    if (event?.name || !unitId || !eventId) return;
    let cancelled = false;
    getDoc(doc(db, 'units', unitId, 'events', eventId))
      .then((snap) => {
        const name = snap.exists() ? (snap.data() as { name?: unknown }).name : undefined;
        if (!cancelled && typeof name === 'string') setLoadedEventName(name);
      })
      .catch((error) => console.error('Failed to load event name:', error));
    return () => {
      cancelled = true;
    };
  }, [event?.name, unitId, eventId]);

  useEffect(() => {
    if (!unitLogoUrl) {
      setUnitLogoImage(null);
      setElements((prev) => {
        let changed = false;
        const next = prev.map((el) => {
          if (el.type !== 'unitLogo') {
            return el;
          }
          if (!el.unitLogoUrl && !el.imageData) {
            return el;
          }
          changed = true;
          return { ...el, unitLogoUrl: undefined, imageUrl: undefined, imageData: undefined };
        });
        return changed ? next : prev;
      });
      return;
    }

    let cancelled = false;
    const logoImage = new window.Image();
    logoImage.crossOrigin = 'anonymous';
    logoImage.onload = () => {
      if (cancelled) return;
      setUnitLogoImage(logoImage);
      setElements((prev) => {
        let changed = false;
        const next = prev.map((el) => {
          if (el.type !== 'unitLogo') {
            return el;
          }
          if (el.unitLogoUrl === unitLogoUrl && el.imageData === logoImage) {
            return el;
          }
          changed = true;
          return {
            ...el,
            unitLogoUrl,
            imageUrl: unitLogoUrl,
            imageData: isRenderableImage(logoImage) ? logoImage : undefined,
          };
        });
        return changed ? next : prev;
      });
    };
    logoImage.onerror = (error) => {
      if (cancelled) return;
      console.error('Failed to load unit logo image:', error);
      setUnitLogoImage(null);
    };
    logoImage.src = unitLogoUrl;

    return () => {
      cancelled = true;
    };
  }, [unitLogoUrl]);

  // Load floor plan on mount
  useEffect(() => {
    loadFloorPlan();
  }, [loadFloorPlan]);

  // New rooms get an empty canvas slot (while designer is open, parent rooms can update)
  useEffect(() => {
    if (isLoading) return;
    for (const id of floorPlanTabRoomIds) {
      if (!floorPlanRoomCacheRef.current[id]) {
        floorPlanRoomCacheRef.current[id] = {
          elements: [],
          zoom: 1,
          panX: 0,
          panY: 0,
          paperSize: 'A4',
          orientation: 'portrait',
          frameStyle: 'none',
        };
      }
    }
  }, [isLoading, floorPlanTabRoomIds]);

  useEffect(() => {
    if (isLoading || activeFloorPlanRoomId === null) return;
    if (!floorPlanTabRoomIds.includes(activeFloorPlanRoomId)) {
      switchFloorPlanRoom(floorPlanTabRoomIds[0]);
    }
  }, [isLoading, floorPlanTabRoomIds, activeFloorPlanRoomId, switchFloorPlanRoom]);

  // Auto-save on state changes (debounced)
  useEffect(() => {
    if (isLoading) return; // Don't auto-save while loading
    if (!unitId || !eventId) return; // Don't auto-save without IDs

    // Mark as unsaved when changes are detected
    setSaveStatus('unsaved');
    hasPendingSaveRef.current = true;

    const timer = setTimeout(() => {
      autoSaveFloorPlan().catch(() => {
        /* status and message are shown in the header */
      });
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(timer);
    // roomCacheRevision: other tabs changed in the background (their state lives in a ref)
  }, [autoSaveFloorPlan, isLoading, unitId, eventId, roomCacheRevision]);

  const autoSaveFloorPlanRef = useRef(autoSaveFloorPlan);
  autoSaveFloorPlanRef.current = autoSaveFloorPlan;

  // Unmounting cancels the debounce timer above, so write any change made in the last second
  useEffect(
    () => () => {
      if (hasPendingSaveRef.current) {
        autoSaveFloorPlanRef.current().catch(() => {
          /* already logged; the designer is closed */
        });
      }
    },
    []
  );

  /** Close only once the latest changes are stored, or the user chooses to discard them. */
  const handleClose = async () => {
    if (hasPendingSaveRef.current || saveStatus === 'error') {
      try {
        await autoSaveFloorPlan();
      } catch (error) {
        const result = await confirmAction({
          title: 'Design not saved',
          text: `Your latest changes could not be saved: ${error instanceof Error ? error.message : 'Unknown error'} Close anyway and lose them?`,
          confirmButtonText: 'Close without saving',
          icon: 'warning',
        });
        if (!result.isConfirmed) return;
        hasPendingSaveRef.current = false;
      }
    }
    onClose();
  };

  const dimensions = getPageDimensions(paperSize, orientation);

  /**
   * Change paper size/orientation and carry the design across: positions and boxes scale with the
   * page, images keep their aspect ratio, and text grows or shrinks with the page area.
   */
  const changePageSetup = (nextPaperSize: PaperSize, nextOrientation: Orientation) => {
    if (nextPaperSize === paperSize && nextOrientation === orientation) return;
    const next = getPageDimensions(nextPaperSize, nextOrientation);
    const sx = next.width / dimensions.width;
    const sy = next.height / dimensions.height;
    const uniform = Math.min(sx, sy);
    const textScale = Math.sqrt(sx * sy);
    const pxPerMmNow = Math.min(
      previewDimensions.width / dimensions.width,
      previewDimensions.height / dimensions.height
    );
    const scaleFont = (size: number | undefined) =>
      size === undefined ? undefined : Math.round(size * textScale * 10) / 10;

    const newElements = elements.map((el) => {
      const keepsAspect = el.type === 'image' || el.type === 'unitLogo';
      const width = keepsAspect ? el.width * uniform : el.width * sx;
      const height = keepsAspect ? el.height * uniform : el.height * sy;
      const centreX = (el.x + el.width / 2) * sx;
      const centreY = (el.y + el.height / 2) * sy;
      const scaled: CanvasElement = {
        ...el,
        width,
        height,
        x: Math.max(0, Math.min(next.width - width, centreX - width / 2)),
        y: Math.max(0, Math.min(next.height - height, centreY - height / 2)),
        fontSize: scaleFont(el.fontSize),
        guestListFontSize: scaleFont(el.guestListFontSize),
      };
      if (el.type === 'tablePlan' && !el.tablePlanFit) {
        // Older plans store zoom/pan in screen pixels; convert to page units first so they scale with the page
        const unit = el.tablePlanUnits === 'page' ? 1 : 1 / pxPerMmNow;
        scaled.tablePlanUnits = 'page';
        scaled.tablePlanScale = (el.tablePlanScale || 1) * unit * uniform;
        scaled.tablePlanX = (el.tablePlanX || 0) * unit * uniform;
        scaled.tablePlanY = (el.tablePlanY || 0) * unit * uniform;
      }
      return scaled;
    });

    setPaperSize(nextPaperSize);
    setOrientation(nextOrientation);
    setElements(newElements);
    saveToHistory(newElements, undefined, { paperSize: nextPaperSize, orientation: nextOrientation });
  };

  // Calculate preview dimensions
  useEffect(() => {
    const updatePreviewSize = () => {
      if (previewContainerRef.current) {
        const container = previewContainerRef.current;
        // Use almost the full container space with minimal padding
        const containerWidth = container.clientWidth - 20;
        const containerHeight = container.clientHeight - 20;
        
        const widthScale = containerWidth / dimensions.width;
        const heightScale = containerHeight / dimensions.height;
        const fitScale = Math.min(widthScale, heightScale);
        
        // Use the full available space, but cap pixel buffer size for smoother editing.
        let previewWidth = Math.floor(dimensions.width * fitScale);
        let previewHeight = Math.floor(dimensions.height * fitScale);
        const maxDim = Math.max(previewWidth, previewHeight);
        if (maxDim > MAX_DESIGNER_PREVIEW_PX) {
          const s = MAX_DESIGNER_PREVIEW_PX / maxDim;
          previewWidth = Math.floor(previewWidth * s);
          previewHeight = Math.floor(previewHeight * s);
        }

        setPreviewDimensions({ width: previewWidth, height: previewHeight });
      }
    };

    const timer = setTimeout(() => {
      updatePreviewSize();
    }, 100);

    // Use passive listener for better performance
    window.addEventListener('resize', updatePreviewSize, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePreviewSize);
    };
  }, [dimensions.width, dimensions.height]);

  /**
   * Images kept inline as data URLs (designs saved by older versions, or pasted from an older copy)
   * are moved to Cloud Storage in the background so they stop counting towards the 1 MiB document.
   * An image stays inline if its upload fails; the size check on save still reports that.
   */
  useEffect(() => {
    if (isLoading || !unitId || !eventId) return;
    const inlineUrls = new Set<string>();
    const collect = (elts: CanvasElement[]) => {
      for (const el of elts) {
        if (el.type === 'image' && isInlineImageUrl(el.imageUrl)) inlineUrls.add(el.imageUrl);
      }
    };
    collect(elements);
    Object.values(floorPlanRoomCacheRef.current).forEach((room) => collect(room.elements));

    for (const dataUrl of inlineUrls) {
      let upload = inlineImageUploadsRef.current.get(dataUrl);
      if (!upload) {
        upload = dataUrlToBlob(dataUrl)
          .then((blob) => uploadDesignImage(blob))
          .catch((error) => {
            console.error('Failed to move floor plan image to storage:', error);
            return null;
          });
        inlineImageUploadsRef.current.set(dataUrl, upload);
      }
      void upload.then((storedUrl) => {
        if (!storedUrl) return;
        const swap = (elts: CanvasElement[]) =>
          elts.some((el) => el.imageUrl === dataUrl)
            ? elts.map((el) => (el.imageUrl === dataUrl ? { ...el, imageUrl: storedUrl } : el))
            : elts;
        let cacheChanged = false;
        for (const [key, room] of Object.entries(floorPlanRoomCacheRef.current)) {
          const next = swap(room.elements);
          if (next !== room.elements) {
            floorPlanRoomCacheRef.current[key] = { ...room, elements: next };
            cacheChanged = true;
          }
        }
        setElements((prev) => swap(prev));
        if (cacheChanged) setRoomCacheRevision((n) => n + 1);
      });
    }
  }, [elements, isLoading, unitId, eventId, uploadDesignImage]);

  // Load pictures for image elements that only have a URL (pasted, restored by undo, or not yet loaded)
  const imageLoadsRef = useRef(new Set<string>());
  useEffect(() => {
    for (const el of elements) {
      const url = el.imageUrl;
      if (el.type !== 'image' || !url || isRenderableImage(el.imageData) || imageLoadsRef.current.has(url)) {
        continue;
      }
      // Remember the URL even after it settles so a broken image is not retried on every render
      imageLoadsRef.current.add(url);
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const source = downscaleImageForDesignerTexture(img);
        setElements((prev) =>
          prev.map((p) =>
            p.imageUrl === url && !isRenderableImage(p.imageData) ? { ...p, imageData: source as HTMLImageElement | HTMLCanvasElement } : p
          )
        );
        // Let a later element with this URL (e.g. after undo) load again from the browser cache
        imageLoadsRef.current.delete(url);
      };
      img.onerror = () => console.error('Failed to load image:', url);
      img.src = url;
    }
  }, [elements]);

  // Element manipulation functions
  const copyElements = useCallback(() => {
    const selectedElements = elements.filter(el => selectedElementIds.includes(el.id));
    const serialisable = selectedElements.map(({ imageData: _imageData, ...rest }) => rest);
    try {
      localStorage.setItem('copiedElements', JSON.stringify(serialisable));
      showSuccess('Elements copied');
    } catch (error) {
      console.error('Failed to copy elements:', error);
      showError('Could not copy these elements (too large for the clipboard store).');
    }
  }, [elements, selectedElementIds]);

  const pasteElements = useCallback(() => {
    let copiedData: string | null = null;
    try {
      copiedData = localStorage.getItem('copiedElements');
    } catch {
      copiedData = null;
    }
    if (copiedData) {
      let copiedElements: CanvasElement[];
      try {
        copiedElements = JSON.parse(copiedData);
      } catch {
        return;
      }
      if (!Array.isArray(copiedElements) || copiedElements.length === 0) return;
      const newElements = copiedElements.map(el => ({
        ...el,
        id: `element-${Date.now()}-${Math.random()}`,
        x: snapValue(el.x + 20),
        y: snapValue(el.y + 20),
      }));
      
      const allElements = [...elements, ...newElements];
      setElements(allElements);
      setSelectedElementIds(newElements.map(el => el.id));
      saveToHistory(allElements);
      showSuccess('Elements pasted');
    }
  }, [elements, saveToHistory]);

  const duplicateElements = useCallback((ids?: string[]) => {
    const targetIds = ids && ids.length > 0 ? ids : selectedElementIds;
    if (targetIds.length === 0) {
      return;
    }

    const selectedElements = elements.filter(el => targetIds.includes(el.id));
    if (selectedElements.length === 0) {
      return;
    }

    const newElements = selectedElements.map(el => ({
      ...el,
      id: `element-${Date.now()}-${Math.random()}`,
      x: snapValue(el.x + 20),
      y: snapValue(el.y + 20),
    }));
    
    const allElements = [...elements, ...newElements];
    setElements(allElements);
    setSelectedElementIds(newElements.map(el => el.id));
    saveToHistory(allElements);
  }, [elements, selectedElementIds, saveToHistory]);

  const deleteSelectedElements = useCallback((ids?: string[]) => {
    const targetIds = ids && ids.length > 0 ? ids : selectedElementIds;
    if (targetIds.length === 0) {
      return;
    }

    const newElements = elements.filter((el) => !targetIds.includes(el.id));
    setElements(newElements);
    setSelectedElementIds((prev) => prev.filter(id => !targetIds.includes(id)));
    saveToHistory(newElements);
  }, [elements, selectedElementIds, saveToHistory]);

  const setLockStateForElements = useCallback((locked: boolean, ids?: string[]) => {
    const targetIds = ids && ids.length > 0 ? ids : selectedElementIds;
    if (targetIds.length === 0) {
      return;
    }

    const newElements = elements.map((el) => {
      if (!targetIds.includes(el.id)) {
        return el;
      }

      if (el.locked === locked) {
        return el;
      }

      return { ...el, locked };
    });

    setElements(newElements);
    saveToHistory(newElements);
  }, [elements, selectedElementIds, saveToHistory]);

  const lockSelectedElements = useCallback(() => {
    setLockStateForElements(true);
  }, [setLockStateForElements]);

  const unlockSelectedElements = useCallback(() => {
    setLockStateForElements(false);
  }, [setLockStateForElements]);

  const rotateSelectedElements = useCallback((delta: number) => {
    if (selectedElementIds.length === 0) {
      return;
    }

    const newElements = elements.map((el) => {
      if (!selectedElementIds.includes(el.id) || el.locked) {
        return el;
      }

      const currentRotation = typeof el.rotation === 'number' ? el.rotation : 0;
      const nextRotation = (currentRotation + delta) % 360;
      return { ...el, rotation: nextRotation < 0 ? nextRotation + 360 : nextRotation };
    });

    setElements(newElements);
    saveToHistory(newElements);
  }, [elements, selectedElementIds, saveToHistory]);

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu) {
        setContextMenu(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu]);

  // Lock page scroll while the modal is open. The sidebar and canvas keep their own scrolling and wheel handling.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Stop arrow keys scrolling the page; fields and widgets keep their own arrow-key behaviour
      if (ARROW_KEYS.includes(e.key) && !isEditableKeyTarget(e.target)) {
        e.preventDefault();
      }
    };

    const handleWheel = (e: WheelEvent) => {
      // Ctrl/Cmd + wheel would zoom the whole browser page instead of the design
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('wheel', handleWheel, { passive: false, capture: true });

    const { body } = document;
    const previous = {
      overflow: body.style.overflow,
      position: body.style.position,
      width: body.style.width,
      top: body.style.top,
    };
    const scrollY = window.scrollY;
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.width = '100%';
    body.style.top = `-${scrollY}px`;

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('wheel', handleWheel, true);
      body.style.overflow = previous.overflow;
      body.style.position = previous.position;
      body.style.width = previous.width;
      body.style.top = previous.top;
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Typing in a field (element name, text content, numbers, sliders, dropdowns) must not act on the canvas
      if (isEditableKeyTarget(e.target)) return;
      // Another dialog (e.g. new design plan) is on top of the designer
      if (showCreateDesignModal || renameDesignTarget) return;

      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'z':
            e.preventDefault();
            if (e.shiftKey) {
              redo();
            } else {
              undo();
            }
            break;
          case 'y':
            e.preventDefault();
            redo();
            break;
          case 'c':
            if (selectedElementIds.length > 0) {
              e.preventDefault();
              copyElements();
            }
            break;
          case 'v':
            e.preventDefault();
            pasteElements();
            break;
          case 'd':
            if (selectedElementIds.length > 0) {
              e.preventDefault();
              duplicateElements();
            }
            break;
          case 'a':
            e.preventDefault();
            setSelectedElementIds(elements.filter(el => el.visible).map(el => el.id));
            break;
          case 's': {
            e.preventDefault();
            // Ctrl+S exports, through the export button so it behaves the same
            if (previewStageRef.current && elements.length > 0) {
              const exportButton = document.querySelector('[data-export-button]') as HTMLButtonElement;
              exportButton?.click();
            }
            break;
          }
        }
      }
      
      if (e.key === 'Delete' && selectedElementIds.length > 0) {
        e.preventDefault();
        deleteSelectedElements();
      }
      
      if (e.key === 'Escape') {
        setSelectedElementIds([]);
        setContextMenu(null);
      }
      
      // Arrow keys for element movement
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && selectedElementIds.length > 0) {
        e.preventDefault();
        const moveDistance = e.shiftKey ? 10 : 1; // Shift for larger moves
        
        // Move each selected element relative to its current position
        const newElements = elements.map(el => {
          if (!selectedElementIds.includes(el.id) || el.locked) return el;
          
          let newX = el.x;
          let newY = el.y;
          
          switch (e.key) {
            case 'ArrowUp':
              newY = el.y - moveDistance;
              break;
            case 'ArrowDown':
              newY = el.y + moveDistance;
              break;
            case 'ArrowLeft':
              newX = el.x - moveDistance;
              break;
            case 'ArrowRight':
              newX = el.x + moveDistance;
              break;
          }
          
          // Ensure element stays within canvas bounds
          const clampedX = Math.max(0, Math.min(dimensions.width - el.width, newX));
          const clampedY = Math.max(0, Math.min(dimensions.height - el.height, newY));
          
          return { ...el, x: clampedX, y: clampedY };
        });
        
        setElements(newElements);
        saveToHistory(newElements);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElementIds, elements, undo, redo, copyElements, pasteElements, duplicateElements, deleteSelectedElements, saveToHistory, showCreateDesignModal, renameDesignTarget]);

  // Update transformer
  useEffect(() => {
    if (transformerRef.current && previewStageRef.current) {
      const stage = previewStageRef.current;
      // Locked elements keep their selection outline but get no resize/rotate handles
      const selectedNodes = selectedElementIds
        .filter(id => !elements.find(el => el.id === id)?.locked)
        .map(id => stage.findOne((node: any) => node.attrs.id === id))
        .filter((node): node is Konva.Node => node !== null && node !== undefined);
      
      transformerRef.current.nodes(selectedNodes);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [selectedElementIds, elements]);

  const snapValue = (value: number) => {
    if (snapToGrid) {
      return Math.round(value / GRID_SIZE) * GRID_SIZE;
    }
    return value;
  };

  // Get elements at position (for click detection)
  const getElementsAtPosition = (x: number, y: number): CanvasElement[] => {
    const scaleX = (previewDimensions.width / dimensions.width) * zoom;
    const scaleY = (previewDimensions.height / dimensions.height) * zoom;
    
    // Convert stage coordinates (after designer zoom/pan) to document coordinates
    const docX = (x - panX) / scaleX;
    const docY = (y - panY) / scaleY;
    
    // Find all visible elements at this position (in reverse order - topmost first)
    return [...elements].reverse().filter(element => {
      if (!element.visible) return false;
      
      // Simple bounding box check
      return docX >= element.x && 
             docX <= element.x + element.width && 
             docY >= element.y && 
             docY <= element.y + element.height;
    });
  };

  // Handle stage click
  const handleStageClick = (e: any) => {
    try {
      // Check if we clicked on the stage background
      if (e.target === e.target.getStage()) {
        setSelectedElementIds([]);
        return;
      }
    } catch (error) {
      console.error('Error handling stage click:', error);
    }
  };

  /** Zoom the design, keeping the given stage point (default: centre) fixed on screen. */
  const zoomTo = (nextZoom: number, anchor?: { x: number; y: number }) => {
    const clamped = Math.max(MIN_DESIGNER_ZOOM, Math.min(MAX_DESIGNER_ZOOM, Math.round(nextZoom * 100) / 100));
    if (clamped === zoom) return;
    const point = anchor ?? { x: previewDimensions.width / 2, y: previewDimensions.height / 2 };
    const ratio = clamped / zoom;
    setPanX(Math.round(point.x - (point.x - panX) * ratio));
    setPanY(Math.round(point.y - (point.y - panY) * ratio));
    setZoom(clamped);
  };

  // Handle stage wheel for zoom around the pointer
  const handleStageWheel = (e: any) => {
    e.evt.preventDefault();
    const pointer = e.target.getStage()?.getPointerPosition() ?? undefined;
    zoomTo(e.evt.deltaY > 0 ? zoom / DESIGNER_ZOOM_STEP : zoom * DESIGNER_ZOOM_STEP, pointer);
  };

  // Handle stage drag for panning (only when clicking empty space; if over a selected element, move that instead)
  const handleStageMouseDown = (e: any) => {
    const stage = e.target.getStage();
    if (e.target !== stage) return;

    if (selectedElementIds.length > 0) {
      const pos = stage.getPointerPosition();
      if (pos) {
        const scaleX = (previewDimensions.width / dimensions.width) * zoom;
        const scaleY = (previewDimensions.height / dimensions.height) * zoom;
        const designX = (pos.x - panX) / scaleX;
        const designY = (pos.y - panY) / scaleY;
        const hitElement = elements.find(
          (el) =>
            selectedElementIds.includes(el.id) &&
            designX >= el.x &&
            designX <= el.x + el.width &&
            designY >= el.y &&
            designY <= el.y + el.height
        );
        if (hitElement) {
          const node = stage.findOne('#' + hitElement.id);
          if (node && node.getClassName() === 'Group' && !elements.find((el) => el.id === hitElement.id)?.locked) {
            node.startDrag(e.evt);
          }
          return;
        }
      }
    }

    setIsDragging(true);
    setDragStart({ x: e.evt.clientX - panX, y: e.evt.clientY - panY });
  };

  const handleStageMouseMove = (e: any) => {
    if (isDragging) {
      const newPanX = Math.round(e.evt.clientX - dragStart.x);
      const newPanY = Math.round(e.evt.clientY - dragStart.y);
      setPanX(newPanX);
      setPanY(newPanY);
    }
  };

  const handleStageMouseUp = () => {
    setIsDragging(false);
  };

  // Handle stage context menu (right-click)
  const handleStageContextMenu = (e: any) => {
    try {
      e.evt.preventDefault();
      
      const stage = e.target.getStage();
      if (!stage) return;
      
      const pointerPosition = stage.getPointerPosition();
      if (!pointerPosition) return;
      
      const elementsAtPosition = getElementsAtPosition(pointerPosition.x, pointerPosition.y);
      
      if (elementsAtPosition.length > 1) {
        // Multiple elements - show context menu
        setContextMenu({
          x: e.evt.clientX,
          y: e.evt.clientY,
          elements: elementsAtPosition
        });
      } else if (elementsAtPosition.length === 1) {
        // Single element - just select it
        setSelectedElementIds([elementsAtPosition[0].id]);
      }
    } catch (error) {
      console.error('Error handling context menu:', error);
    }
  };

  const getElementTypeLabel = (type: CanvasElement['type']): string => ELEMENT_TYPE_LABELS[type];

  const addElement = (type: CanvasElement['type']) => {
    if (type === 'unitLogo' && !unitLogoUrl) {
      showError('No unit logo available. Upload a logo in Unit Settings to use this element.');
      return;
    }

    // Calculate default size as half of canvas dimensions
    const defaultWidth = dimensions.width / 2;
    const defaultHeight = dimensions.height / 2;
    
    // Adjust sizes based on element type
    let width: number;
    let height: number;
    
    switch (type) {
      case 'text':
        width = defaultWidth * 0.6; // 30% of canvas width
        height = defaultHeight * 0.2; // 10% of canvas height
        break;
      case 'image':
      case 'unitLogo': {
        if (type === 'unitLogo' && unitLogoImage && unitLogoImage.naturalWidth > 0 && unitLogoImage.naturalHeight > 0) {
          const imageDims = computeImageDimensionsForCanvas(
            unitLogoImage.naturalWidth,
            unitLogoImage.naturalHeight,
            dimensions.width,
            dimensions.height,
          );
          width = imageDims.width;
          height = imageDims.height;
        } else {
          width = defaultWidth * 0.8;
          height = defaultHeight * 0.8;
        }
        break;
      }
      case 'tablePlan':
        // Fill the page; the plan itself is fitted to these bounds at render time
        width = dimensions.width;
        height = dimensions.height;
        break;
      case 'guestList':
      case 'guestListSurname':
        width = defaultWidth;
        height = defaultHeight;
        break;
      case 'shape':
        width = defaultWidth * 0.4; // 20% of canvas
        height = defaultHeight * 0.4;
        break;
      default:
        width = defaultWidth;
        height = defaultHeight;
    }
    
    // Center the element on canvas
    const x = snapValue((dimensions.width - width) / 2);
    const y = snapValue((dimensions.height - height) / 2);

    const name = `${ELEMENT_BASE_NAMES[type]} ${elements.filter(el => el.type === type).length + 1}`;

    const newElement: CanvasElement = {
      id: `element-${Date.now()}`,
      type,
      x,
      y,
      width,
      height,
      rotation: 0,
      opacity: 1,
      locked: false,
      visible: true,
      name,
      content: type === 'text' ? 'Enter text here' : undefined,
      fontSize: type === 'text' ? 32 : undefined,
      fontFamily: type === 'text' ? 'Arial' : undefined,
      fontWeight: type === 'text' ? 'bold' : undefined,
      fontStyle: type === 'text' ? 'normal' : undefined,
      textDecoration: type === 'text' ? 'none' : undefined,
      textAlign: type === 'text' ? 'center' : undefined,
      textColor: type === 'text' ? '#000000' : undefined,
      backgroundColor: type === 'text' ? 'transparent' : undefined,
      guestListFontSize: type === 'guestList' || type === 'guestListSurname' ? 10 : undefined,
      guestListFontFamily: type === 'guestList' || type === 'guestListSurname' ? 'Arial' : undefined,
      guestListFontWeight: type === 'guestList' || type === 'guestListSurname' ? 'normal' : undefined,
      guestListColumns: type === 'guestList' ? {
        name: true,
        seat: true,
      } : undefined,
      guestListDirection: type === 'guestList' ? 'across' : undefined,
      guestListRoom:
        type === 'guestList' || type === 'guestListSurname'
          ? effectiveDesignerTabRoomId
          : undefined,
      // Unset = every table in the room, including tables added later; set once the user reorders/removes
      guestListSelectedTables: undefined,
      guestListHiddenTables: type === 'guestList' || type === 'guestListSurname' ? [] : undefined,
      guestListShowDividers: type === 'guestList' ? true : undefined,
      guestListSurnameColumnCount: type === 'guestListSurname' ? 1 : undefined,
      shapeType: type === 'shape' ? 'rectangle' : undefined,
      fillColor: type === 'shape' ? '#3b82f6' : undefined,
      strokeColor: type === 'shape' ? '#1e3a8a' : undefined,
      strokeWidth: type === 'shape' ? 2 : undefined,
      // Table plan properties (vector-based rendering)
      tablePlanScale: type === 'tablePlan' ? 0.1 : undefined, // 10% zoom by default
      tablePlanX: type === 'tablePlan' ? 0 : undefined, // No pan offset
      tablePlanY: type === 'tablePlan' ? 0 : undefined, // No pan offset
      tablePlanRoom: type === 'tablePlan' ? effectiveDesignerTabRoomId : undefined,
      tablePlanFit: type === 'tablePlan' ? true : undefined,
    };

    if (type === 'unitLogo') {
      newElement.unitLogoUrl = unitLogoUrl ?? undefined;
      newElement.imageUrl = unitLogoUrl ?? undefined;
      if (isRenderableImage(unitLogoImage)) {
        newElement.imageData = unitLogoImage;
        const ratio = getElementNativeAspectRatio(newElement);
        if (ratio) {
          newElement.nativeAspectRatio = ratio;
        }
      }
    }
    
    const newElements = [...elements, newElement];
    setElements(newElements);
    setSelectedElementIds([newElement.id]);
    saveToHistory(newElements);
    
  };

  const updateElement = (id: string, updates: Partial<CanvasElement>) => {
    const newElements = elements.map((el) => {
      if (el.id !== id) return el;
      
      // Validate and clamp values
      const validatedUpdates = { ...updates };
      
      // Clamp position and size to reasonable values
      if (validatedUpdates.x !== undefined) {
        validatedUpdates.x = Math.max(0, Math.min(dimensions.width, validatedUpdates.x));
      }
      if (validatedUpdates.y !== undefined) {
        validatedUpdates.y = Math.max(0, Math.min(dimensions.height, validatedUpdates.y));
      }
      if (validatedUpdates.width !== undefined) {
        validatedUpdates.width = Math.max(10, Math.min(dimensions.width, validatedUpdates.width));
      }
      if (validatedUpdates.height !== undefined) {
        validatedUpdates.height = Math.max(10, Math.min(dimensions.height, validatedUpdates.height));
      }
      if (validatedUpdates.opacity !== undefined) {
        validatedUpdates.opacity = Math.max(0, Math.min(1, validatedUpdates.opacity));
      }
      if (validatedUpdates.fontSize !== undefined) {
        validatedUpdates.fontSize = Math.max(1, Math.min(200, validatedUpdates.fontSize));
      }
      
      return { ...el, ...validatedUpdates };
    });
    setElements(newElements);
    saveToHistory(newElements, `update:${id}:${Object.keys(updates).sort().join(',')}`);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    // Clear so choosing the same file again still fires a change event
    input.value = '';
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Please select a valid image file');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showError('Image file is too large. Please select an image smaller than 10MB');
      return;
    }

    const localUrl = URL.createObjectURL(file);
    // The new element draws from this image directly unless it is downscaled into a canvas
    let keepLocalUrl = false;
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Failed to load image'));
        image.src = localUrl;
      });

      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      if (naturalWidth <= 0 || naturalHeight <= 0) {
        showError('Failed to read image dimensions');
        return;
      }

      setIsUploadingImage(true);
      let imageUrl: string;
      try {
        imageUrl = await uploadDesignImage(file);
      } catch (error) {
        console.error('Failed to upload floor plan image:', error);
        showError('Failed to upload image. Please try again.');
        return;
      } finally {
        setIsUploadingImage(false);
      }

      const imageDims = computeImageDimensionsForCanvas(
        naturalWidth,
        naturalHeight,
        dimensions.width,
        dimensions.height,
      );
      const width = snapValue(imageDims.width);
      const height = snapValue(imageDims.height);

      const imageSource = downscaleImageForDesignerTexture(img);
      keepLocalUrl = !(imageSource instanceof HTMLCanvasElement);

      // Center on canvas
      const x = snapValue((dimensions.width - width) / 2);
      const y = snapValue((dimensions.height - height) / 2);

      const newElement: CanvasElement = {
        id: `element-${Date.now()}`,
        type: 'image',
        x,
        y,
        width,
        height,
        rotation: 0,
        opacity: 1,
        locked: false,
        visible: true,
        name: `Image ${elementsRef.current.filter(el => el.type === 'image').length + 1}`,
        imageUrl,
        imageData: imageSource instanceof HTMLCanvasElement ? imageSource : img,
        nativeAspectRatio: imageDims.nativeAspectRatio,
      };

      // The upload may have taken a while; add to the latest elements, not the ones from when it started
      const newElements = [...elementsRef.current, newElement];
      setElements(newElements);
      setSelectedElementIds([newElement.id]);
      saveToHistory(newElements);
      showSuccess('Image added successfully');
    } catch (error) {
      console.error('Failed to add image:', error);
      showError('Failed to load image');
    } finally {
      if (!keepLocalUrl) URL.revokeObjectURL(localUrl);
    }
  };

  const resetImageAspectRatio = () => {
    if (selectedElementIds.length === 0) return;

    const newElements = elements.map((el) => {
      if (!selectedElementIds.includes(el.id)) return el;
      if (el.type !== 'image' && el.type !== 'unitLogo') return el;
      if (el.locked) return el;

      const ratio = getElementNativeAspectRatio(el);
      if (!ratio) return el;

      const centerX = el.x + el.width / 2;
      const centerY = el.y + el.height / 2;
      const newWidth = el.width;
      const newHeight = Math.max(20, newWidth / ratio);

      return {
        ...el,
        width: newWidth,
        height: snapValue(newHeight),
        x: snapValue(centerX - newWidth / 2),
        y: snapValue(centerY - newHeight / 2),
        nativeAspectRatio: ratio,
      };
    });

    setElements(newElements);
    saveToHistory(newElements);
  };

  const hasSelectedImage = selectedElements.some(
    (el) => (el.type === 'image' || el.type === 'unitLogo') && !el.locked,
  );

  const alignElements = (alignment: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => {
    if (selectedElementIds.length === 0) return;

    const selectedElements = elements.filter(el => selectedElementIds.includes(el.id));
    const bounds = {
      left: Math.min(...selectedElements.map(el => el.x)),
      right: Math.max(...selectedElements.map(el => el.x + el.width)),
      top: Math.min(...selectedElements.map(el => el.y)),
      bottom: Math.max(...selectedElements.map(el => el.y + el.height)),
    };

    const newElements = elements.map(el => {
      if (!selectedElementIds.includes(el.id) || el.locked) return el;

      switch (alignment) {
        case 'left':
          return { ...el, x: bounds.left };
        case 'center':
          return { ...el, x: (bounds.left + bounds.right) / 2 - el.width / 2 };
        case 'right':
          return { ...el, x: bounds.right - el.width };
        case 'top':
          return { ...el, y: bounds.top };
        case 'middle':
          return { ...el, y: (bounds.top + bounds.bottom) / 2 - el.height / 2 };
        case 'bottom':
          return { ...el, y: bounds.bottom - el.height };
        default:
          return el;
      }
    });

    setElements(newElements);
    saveToHistory(newElements);
  };

  const bringToFront = (ids: string[]) => {
    const selected = elements.filter(el => ids.includes(el.id));
    const others = elements.filter(el => !ids.includes(el.id));
    const newElements = [...others, ...selected];
    setElements(newElements);
    saveToHistory(newElements);
  };

  const sendToBack = (ids: string[]) => {
    const selected = elements.filter(el => ids.includes(el.id));
    const others = elements.filter(el => !ids.includes(el.id));
    const newElements = [...selected, ...others];
    setElements(newElements);
    saveToHistory(newElements);
  };

  const handleExport = async () => {
    const stage = previewStageRef.current;
    if (!stage) {
      showError('No content to export');
      return;
    }

    if (elements.length === 0) {
      showError('No elements to export. Add some content first.');
      return;
    }

    // Hide selection and grid so the export image matches the design only
    flushSync(() => {
      setIsExporting(true);
      setSelectedElementIds([]);
    });
    await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

    try {
      const pdf = new jsPDF({
        orientation: orientation === 'landscape' ? 'l' : 'p',
        unit: 'mm',
        format: paperSize.toLowerCase() as any,
      });

      const pageWidthMm = dimensions.width;
      const pageHeightMm = dimensions.height;

      // Capture at current preview size (no resize) so the user never sees a wrong/cropped frame.
      // Use pixelRatio to get print-quality resolution for the PDF.
      const MM_PER_INCH = 25.4;
      const PRINT_DPI = 300;
      const targetPixelsW = Math.round((pageWidthMm / MM_PER_INCH) * PRINT_DPI);
      const targetPixelsH = Math.round((pageHeightMm / MM_PER_INCH) * PRINT_DPI);
      // Need enough pixels on both axes for full-page print; use the limiting ratio (max).
      const ratioW = targetPixelsW / previewDimensions.width;
      const ratioH = targetPixelsH / previewDimensions.height;
      // Stay within browser canvas limits (Safari refuses canvases over ~16.7 million pixels),
      // which lowers the resolution of A3 and larger pages slightly below 300 DPI.
      const maxRatioForCanvas = Math.sqrt(MAX_EXPORT_CANVAS_PIXELS / (previewDimensions.width * previewDimensions.height));
      const pixelRatio = Math.min(8, maxRatioForCanvas, Math.max(ratioW, ratioH));

      const stageToCapture = previewStageRef.current ?? stage;
      stageToCapture.getLayers().forEach((layer) => layer.draw());

      const dataUrl = stageToCapture.toDataURL({
        mimeType: 'image/png',
        pixelRatio: Math.max(1, pixelRatio),
        quality: 1,
      });

      // Flatten onto white so PDF has no transparency-induced border/shadow
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      const flattenedDataUrl = await new Promise<string>((resolve) => {
        img.onload = () => {
          const w = img.naturalWidth;
          const h = img.naturalHeight;
          const off = document.createElement('canvas');
          off.width = w;
          off.height = h;
          const ctx = off.getContext('2d');
          if (!ctx) {
            resolve(dataUrl);
            return;
          }
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0);
          try {
            resolve(off.toDataURL('image/png', 1));
          } catch {
            resolve(dataUrl);
          }
        };
        img.onerror = () => resolve(dataUrl);
        img.src = dataUrl;
      });

      pdf.addImage(flattenedDataUrl, 'PNG', 0, 0, pageWidthMm, pageHeightMm);
      
      const eventName = (event?.name || loadedEventName || 'export')
        .replace(/[\\/:*?"<>|]+/g, '')
        .replace(/\s+/g, '-')
        .slice(0, 80) || 'export';
      const fileName = `floor-plan-${eventName}-${Date.now()}.pdf`;
      pdf.save(fileName);
      
      showSuccess('PDF exported successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      showError(`Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const renderFrame = () => {
    if (frameStyle === 'none') return null;

    const frameWidth = previewDimensions.width;
    const frameHeight = previewDimensions.height;
    // Frame sizes are in mm (u = pixels per mm) so the PDF does not depend on the screen size
    const u = previewDimensions.width / dimensions.width;
    const margin = 4 * u;

    if (frameStyle === 'simple') {
      return (
        <Rect
          x={margin}
          y={margin}
          width={frameWidth - margin * 2}
          height={frameHeight - margin * 2}
          stroke="#000000"
          strokeWidth={0.8 * u}
          listening={false}
        />
      );
    }

    if (frameStyle === 'elegant') {
      return (
        <Group listening={false}>
          <Rect
            x={margin}
            y={margin}
            width={frameWidth - margin * 2}
            height={frameHeight - margin * 2}
            stroke="#1E3A5F"
            strokeWidth={1.2 * u}
          />
          <Rect
            x={margin + 3 * u}
            y={margin + 3 * u}
            width={frameWidth - margin * 2 - 6 * u}
            height={frameHeight - margin * 2 - 6 * u}
            stroke="#1E3A5F"
            strokeWidth={0.4 * u}
          />
        </Group>
      );
    }

    if (frameStyle === 'decorative') {
      return (
        <Group listening={false}>
          <Rect
            x={margin}
            y={margin}
            width={frameWidth - margin * 2}
            height={frameHeight - margin * 2}
            stroke="#8B5A3C"
            strokeWidth={1.6 * u}
          />
          <Rect
            x={margin + 2.5 * u}
            y={margin + 2.5 * u}
            width={frameWidth - margin * 2 - 5 * u}
            height={frameHeight - margin * 2 - 5 * u}
            stroke="#D4AF37"
            strokeWidth={0.8 * u}
          />
          <Rect
            x={margin + 5 * u}
            y={margin + 5 * u}
            width={frameWidth - margin * 2 - 10 * u}
            height={frameHeight - margin * 2 - 10 * u}
            stroke="#8B5A3C"
            strokeWidth={0.4 * u}
          />
        </Group>
      );
    }

    if (frameStyle === 'masonic') {
      return (
        <Group listening={false}>
          <Rect
            x={margin}
            y={margin}
            width={frameWidth - margin * 2}
            height={frameHeight - margin * 2}
            stroke="#1E3A5F"
            strokeWidth={1.6 * u}
          />
          <Rect
            x={margin + 3 * u}
            y={margin + 3 * u}
            width={frameWidth - margin * 2 - 6 * u}
            height={frameHeight - margin * 2 - 6 * u}
            stroke="#FFFFFF"
            strokeWidth={0.8 * u}
          />
          <Rect
            x={margin + 5 * u}
            y={margin + 5 * u}
            width={frameWidth - margin * 2 - 10 * u}
            height={frameHeight - margin * 2 - 10 * u}
            stroke="#1E3A5F"
            strokeWidth={0.4 * u}
          />
        </Group>
      );
    }

    return null;
  };

  const renderGrid = () => {
    if (!showGrid || isExporting) return null;

    const lines = [];
    const scaleX = previewDimensions.width / dimensions.width;
    const scaleY = previewDimensions.height / dimensions.height;

    for (let i = 0; i <= dimensions.width; i += GRID_SIZE) {
      lines.push(
        <Line
          key={`v-${i}`}
          points={[i * scaleX, 0, i * scaleX, previewDimensions.height]}
          stroke="#e5e7eb"
          strokeWidth={0.5}
          listening={false}
          draggable={false}
          hitStrokeWidth={0}
        />
      );
    }

    for (let i = 0; i <= dimensions.height; i += GRID_SIZE) {
      lines.push(
        <Line
          key={`h-${i}`}
          points={[0, i * scaleY, previewDimensions.width, i * scaleY]}
          stroke="#e5e7eb"
          strokeWidth={0.5}
          listening={false}
          draggable={false}
          hitStrokeWidth={0}
        />
      );
    }

    return <Group listening={false} draggable={false}>{lines}</Group>;
  };

  // Helper function to render a table plan element (vector-based)
  const renderTablePlan = (
    element: CanvasElement,
    roomTables: any[],
    frameWidth: number,
    frameHeight: number,
    pxPerMm: number
  ) => {
    const { scale: tpScale, panX, panY } = resolveTablePlanView(element, roomTables, frameWidth, frameHeight, pxPerMm);
    const tableStrokeWidth = 0.8 * pxPerMm;

    if (roomTables.length === 0) {
      return null;
    }

    // Plan origin: top-left of the tables' bounding box (pan offsets are relative to it)
    let minX = Infinity, minY = Infinity;
    roomTables.forEach(table => {
      minX = Math.min(minX, table.x - table.width / 2);
      minY = Math.min(minY, table.y - table.height / 2);
    });
    
    // Helper function to get contrasting text color for table
    const getContrastingTextColor = (tableColor: string) => {
      // Convert hex to RGB
      const hex = tableColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Calculate luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      
      // Return black for light backgrounds, white for dark backgrounds
      return luminance > 0.5 ? '#000000' : '#ffffff';
    };

    // Helper function to get guest name by RSVP ID
    const getGuestName = (rsvpId: string | null) => {
      if (!rsvpId) {
        return null;
      }
      
      const guest = guests.find(g => g.id === rsvpId);
      
      if (!guest) {
        return null;
      }
      
      const display = guestDisplayName(guest);
      return display ? guestSeatMultilineLabel(display) : null;
    };
    
    // Render tables with chairs and seating
    return (
      <Group>
        {roomTables.map((table: any) => {
          // Normalize coordinates relative to bounding box
          const normalizedX = ((table.x - minX) * tpScale) + panX;
          const normalizedY = ((table.y - minY) * tpScale) + panY;
          const scaledWidth = table.width * tpScale;
          const scaledHeight = table.height * tpScale;
          
          return (
            <Group key={table.id} x={normalizedX} y={normalizedY} rotation={table.rotation}>
              {/* Table shape */}
              {table.type === 'circular' || table.type === 'oval' ? (
                <Ellipse
                  x={0}
                  y={0}
                  radiusX={scaledWidth / 2}
                  radiusY={scaledHeight / 2}
                  fill={table.color || '#ffffff'}
                  stroke="#000000"
                  strokeWidth={tableStrokeWidth}
                  listening={false}
                />
              ) : (
                <Rect
                  x={-scaledWidth / 2}
                  y={-scaledHeight / 2}
                  width={scaledWidth}
                  height={scaledHeight}
                  fill={table.color || '#ffffff'}
                  stroke="#000000"
                  strokeWidth={tableStrokeWidth}
                  cornerRadius={table.type === 'square' ? 5 : 0}
                  listening={false}
                />
              )}
              
              {/* Render chairs and seating */}
              {table.seating && table.seating
                .filter((seat: any) => seat && seat.seatNumber !== undefined)
                .map((seat: any) => {
                  const baseSeatRadius = table.seatSize ?? 28;
                  const seatScale = baseSeatRadius / 28;
                  const seatRadius = baseSeatRadius * tpScale;
                  
                  const guestName = getGuestName(seat.rsvpId);
                  const isOccupied = !!guestName;
                  const defaultChairColor = table.chairColor || '#e5e7eb';
                  const chairColor = defaultChairColor; // Keep designated color, don't change to green
                  const textColor = '#000000'; // Always black text
                  
                  // Scale seat positions
                  const seatX = seat.x * tpScale;
                  const seatY = seat.y * tpScale;
                  const chairRadius = seatRadius;
                  
                  return (
                    <Group key={seat.seatNumber} x={seatX} y={seatY}>
                      {/* Chair */}
                      <Circle
                        radius={chairRadius}
                        fill={chairColor}
                        stroke={isOccupied ? '#000000' : '#9ca3af'}
                        strokeWidth={isOccupied ? 1.5 * tpScale : 1 * tpScale}
                        shadowBlur={isExporting ? 0 : 3 * tpScale}
                        shadowOpacity={isExporting ? 0 : 0.3}
                        listening={false}
                      />
                      
                      {/* Guest name in center of chair (if occupied) */}
                      {guestName && guestName !== 'undefined' && guestName.trim() !== '' && guestName !== 'null' && (
                        (() => {
                          const textWidth = Math.min(chairRadius * 1.75, chairRadius * 2 - 6 * tpScale);
                          const textHeight = textWidth;
                          const offsetX = textWidth / 2;
                          const offsetY = textHeight / 2;
                          const displayName = (guestName || 'Guest').trim();
                          const parts = splitGuestNameIntoSeatLines(displayName);
                          const labelText = guestSeatMultilineLabel(displayName);
                          const lineCount = Math.max(1, parts.length);
                          let nameFont = 9 * seatScale * tpScale;
                          const minFont = 3.5 * tpScale;
                          while (nameFont >= minFont) {
                            const lineH = nameFont * 1.15;
                            const totalH = lineCount * lineH;
                            const longestLine =
                              parts.length > 0
                                ? Math.max(...parts.map((p) => p.length))
                                : displayName.length;
                            const estW = longestLine * nameFont * 0.58;
                            if (estW <= textWidth && totalH <= textHeight) {
                              break;
                            }
                            nameFont -= 0.5 * tpScale;
                          }

                          return (
                            <Text
                              x={0}
                              y={0}
                              text={labelText}
                              fontSize={nameFont}
                              fill={textColor}
                              fontStyle="bold"
                              align="center"
                              verticalAlign="middle"
                              width={textWidth}
                              height={textHeight}
                              offsetX={offsetX}
                              offsetY={offsetY}
                              rotation={-table.rotation}
                              listening={false}
                            />
                          );
                        })()
                      )}
                    </Group>
                  );
                })}
              
              {/* Render seat numbers on table (after chairs so they appear on top) */}
              {table.seating && table.seating
                .filter((seat: any) => seat && seat.seatNumber !== undefined)
                .map((seat: any) => {
                  // Match the on-screen planner logic for seat-number placement
                  const baseSeatRadius = table.seatSize ?? 28;
                  const seatScale = baseSeatRadius / 28;
                  const seatX = seat.x * tpScale;
                  const seatY = seat.y * tpScale;
                  const seatRadius = baseSeatRadius * tpScale;
                  const insideOffset = seatRadius + (12 * seatScale * tpScale);
                  
                  let plateX = seatX;
                  let plateY = seatY;
                  
                  if (table.type === 'circular' || table.type === 'oval') {
                    // Move numbers toward the table center along the seat vector
                    const distance = Math.sqrt((seatX * seatX) + (seatY * seatY));
                    if (distance > 0) {
                      const directionInX = -seatX / distance;
                      const directionInY = -seatY / distance;
                      
                      plateX = seatX + (directionInX * insideOffset);
                      plateY = seatY + (directionInY * insideOffset);
                    }
                  } else {
                    // For rectangular/square tables, move straight toward nearest side
                    const tableHalfWidth = (table.width * tpScale) / 2;
                    const tableHalfHeight = (table.height * tpScale) / 2;
                    
                    const distanceToTop = Math.abs(seatY + tableHalfHeight);
                    const distanceToBottom = Math.abs(seatY - tableHalfHeight);
                    const distanceToLeft = Math.abs(seatX + tableHalfWidth);
                    const distanceToRight = Math.abs(seatX - tableHalfWidth);
                    
                    const minDistance = Math.min(
                      distanceToTop,
                      distanceToBottom,
                      distanceToLeft,
                      distanceToRight
                    );
                    
                    if (minDistance === distanceToTop) {
                      plateX = seatX;
                      plateY = seatY + insideOffset;
                    } else if (minDistance === distanceToBottom) {
                      plateX = seatX;
                      plateY = seatY - insideOffset;
                    } else if (minDistance === distanceToLeft) {
                      plateX = seatX + insideOffset;
                      plateY = seatY;
                    } else {
                      plateX = seatX - insideOffset;
                      plateY = seatY;
                    }
                  }
                  
                  // Get contrasting color for seat number
                  const seatNumberColor = getContrastingTextColor(table.color || '#ffffff');
                  
                  // Calculate precise centering for seat number
                  const seatNumberText = seat.seatNumber.toString();
                  const fontSize = 10 * seatScale * tpScale;
                  
                  // Estimate text dimensions for seat number
                  const textWidth = seatNumberText.length * fontSize * 0.6; // Rough estimate
                  const textHeight = fontSize * 1.2; // Line height
                  
                  // Seat number center should be at the calculated plate position
                  // Offset is half the text dimensions to center it
                  const offsetX = textWidth / 2;
                  const offsetY = textHeight / 2;
                  
                  return (
                    <Text
                      key={`seat-${seat.seatNumber}`}
                      x={plateX}
                      y={plateY}
                      text={seatNumberText}
                      fontSize={fontSize}
                      fill={seatNumberColor}
                      fontStyle="bold"
                      align="center"
                      verticalAlign="middle"
                      width={textWidth}
                      height={textHeight}
                      offsetX={offsetX}
                      offsetY={offsetY}
                      rotation={-table.rotation}
                      listening={false}
                    />
                  );
                })}
              
              {/* Table label */}
              {(() => {
                // Calculate precise centering for table name
                const tableName = table.name;
                const fontSize = 12 * tpScale;
                
                // Estimate text dimensions (approximate)
                const textWidth = tableName.length * fontSize * 0.6; // Rough estimate
                const textHeight = fontSize * 1.2; // Line height
                
                // Table center is at (0, 0) relative to the table group
                // Offset is half the text dimensions to center it
                const offsetX = textWidth / 2;
                const offsetY = textHeight / 2;
                
                return (
                  <Text
                    x={0}
                    y={0}
                    text={tableName}
                    fontSize={fontSize}
                    fill="#000000"
                    align="center"
                    verticalAlign="middle"
                    width={textWidth}
                    height={textHeight}
                    offsetX={offsetX}
                    offsetY={offsetY}
                    rotation={-table.rotation}
                    listening={false}
                  />
                );
              })()}
            </Group>
          );
        })}
      </Group>
    );
  };

  const renderElement = (element: CanvasElement) => {
    if (!element.visible) return null;
    
    const isSelected = selectedElementIds.includes(element.id);
    // Page units (mm) to layer pixels; designer zoom/pan is applied once, by the layer transform
    const scaleX = previewDimensions.width / dimensions.width;
    const scaleY = previewDimensions.height / dimensions.height;
    // During export use exact scale so PDF layout matches designer 1:1; when viewing, round to avoid halo
    const scaledX = isExporting ? element.x * scaleX : Math.round(element.x * scaleX);
    const scaledY = isExporting ? element.y * scaleY : Math.round(element.y * scaleY);
    const scaledWidth = isExporting ? element.width * scaleX : Math.round(element.width * scaleX);
    const scaledHeight = isExporting ? element.height * scaleY : Math.round(element.height * scaleY);

    const handleElementClick = (e: any) => {
      if (isExporting) return;
      
      e.cancelBubble = true;
      
      // If shift key is held, add to selection
      if (e.evt && e.evt.shiftKey) {
        if (selectedElementIds.includes(element.id)) {
          setSelectedElementIds(selectedElementIds.filter(id => id !== element.id));
        } else {
          setSelectedElementIds([...selectedElementIds, element.id]);
        }
      } else {
        setSelectedElementIds([element.id]);
      }
    };

    const handleDragEnd = (e: any) => {
      if (!element.locked) {
        const rawX = e.target.x() / scaleX;
        const rawY = e.target.y() / scaleY;
        
        // Ensure element stays within canvas bounds
        const newX = snapValue(Math.max(0, Math.min(dimensions.width - element.width, rawX)));
        const newY = snapValue(Math.max(0, Math.min(dimensions.height - element.height, rawY)));
        
        const newElements = elements.map((el) =>
          el.id === element.id ? { ...el, x: newX, y: newY } : el
        );
        
        setElements(newElements);
        saveToHistory(newElements);
      }
    };

    const handleTransformEnd = (e: any) => {
      const node = e.target;
      const nodeScaleX = node.scaleX();
      const nodeScaleY = node.scaleY();

      // Calculate new dimensions BEFORE resetting scale
      // The current element dimensions in document coordinates
      const currentWidth = element.width;
      const currentHeight = element.height;
      
      // Apply the scale factor to get new dimensions
      const newWidth = Math.max(20, currentWidth * nodeScaleX);
      const newHeight = Math.max(20, currentHeight * nodeScaleY);
      const newX = snapValue(node.x() / scaleX);
      const newY = snapValue(node.y() / scaleY);
      const newRotation = node.rotation();

      // Reset scale on the node
      node.scaleX(1);
      node.scaleY(1);

      // Update element and save to history
      const newElements = elements.map((el) =>
        el.id === element.id
          ? { ...el, x: newX, y: newY, width: newWidth, height: newHeight, rotation: newRotation }
          : el
      );
      
      setElements(newElements);
      saveToHistory(newElements);
    };

    const onlySelectedDraggable = selectedElementIds.length > 0;
    const draggable = !element.locked && !isExporting && (!onlySelectedDraggable || isSelected);

    const commonProps = {
      id: element.id,
      name: element.id,
      draggable,
      onClick: handleElementClick,
      onTap: handleElementClick,
      onDragEnd: handleDragEnd,
      onTransformEnd: handleTransformEnd,
    };

    if (element.type === 'text') {
      const isSelected = selectedElementIds.includes(element.id);
      
      return (
        <Group
          key={element.id}
          {...commonProps}
          x={scaledX}
          y={scaledY}
          rotation={element.rotation}
          opacity={element.opacity}
        >
          {/* Hit area - transparent rect for clicks */}
          <Rect
            x={0}
            y={0}
            width={scaledWidth}
            height={scaledHeight}
            fill={element.backgroundColor && element.backgroundColor !== 'transparent' ? element.backgroundColor : 'transparent'}
          />
          
          {/* Selection outline */}
          {isSelected && !isExporting && (
            <Rect
              x={-2}
              y={-2}
              width={scaledWidth + 4}
              height={scaledHeight + 4}
              stroke="#3b82f6"
              strokeWidth={2}
              listening={false}
            />
          )}
          
          <Text
            x={0}
            y={0}
            width={scaledWidth}
            height={scaledHeight}
            text={element.content || ''}
            fontSize={(element.fontSize || 16) * Math.min(scaleX, scaleY)}
            fontFamily={element.fontFamily || 'Arial'}
            fontStyle={`${element.fontStyle || 'normal'} ${element.fontWeight || 'normal'}`}
            textDecoration={element.textDecoration || ''}
            fill={element.textColor || '#000000'}
            align={element.textAlign || 'left'}
            verticalAlign="middle"
            padding={10 * Math.min(scaleX, scaleY)}
            listening={false}
          />
        </Group>
      );
    }

    if (element.type === 'image' || element.type === 'unitLogo') {
      const placeholderText = element.type === 'unitLogo' ? 'Logo' : 'Image';
      
      return (
        <Group
          key={element.id}
          {...commonProps}
          x={scaledX}
          y={scaledY}
          rotation={element.rotation}
          opacity={element.opacity}
          shadowEnabled={false}
          shadowBlur={0}
          shadowOpacity={0}
        >
          {/* Hit area — transparent fill so clicks on the logo select the element */}
          <Rect
            x={0}
            y={0}
            width={scaledWidth}
            height={scaledHeight}
            fill="transparent"
            fillEnabled={true}
            strokeEnabled={false}
            strokeWidth={0}
            shadowEnabled={false}
            listening={!isExporting}
          />

          {isRenderableImage(element.imageData) && (
            <KonvaImage
              x={0}
              y={0}
              width={scaledWidth}
              height={scaledHeight}
              image={element.imageData}
              strokeEnabled={false}
              strokeWidth={0}
              shadowEnabled={false}
              shadowBlur={0}
              shadowOpacity={0}
              listening={false}
            />
          )}
          
          {!isRenderableImage(element.imageData) && (
            <Text
              x={0}
              y={scaledHeight / 2 - 10}
              width={scaledWidth}
              text={placeholderText}
              fontSize={14 * Math.min(scaleX, scaleY)}
              fill="#6b7280"
              align="center"
              listening={false}
            />
          )}
          
        </Group>
      );
    }

    if (element.type === 'tablePlan') {
      const isSelected = selectedElementIds.includes(element.id);
      const roomTables = getTablesForPlanOrGuestElement(element.tablePlanRoom);
      
      return (
        <Group
          key={element.id}
          {...commonProps}
          x={scaledX}
          y={scaledY}
          rotation={element.rotation}
          opacity={element.opacity}
        >
          {/* Clipping area for viewport */}
          <Group
            clipX={0}
            clipY={0}
            clipWidth={scaledWidth}
            clipHeight={scaledHeight}
          >
            {/* Background */}
          <Rect
            x={0}
            y={0}
            width={scaledWidth}
            height={scaledHeight}
              fill="transparent"
              stroke={isSelected && !isExporting ? '#3b82f6' : 'transparent'}
              strokeWidth={isSelected && !isExporting ? 2 : 0}
              listening={false}
            />
            
            {/* Render table plan as vector graphics */}
            {roomTables.length > 0 ? (
              renderTablePlan(element, roomTables, scaledWidth, scaledHeight, Math.min(scaleX, scaleY))
            ) : (
          <Text
                x={scaledWidth / 2}
                y={scaledHeight / 2}
                text="No tables in this room"
                fontSize={16}
                fill="#6b7280"
            align="center"
                verticalAlign="middle"
                offsetX={70}
                offsetY={10}
            listening={false}
          />
            )}
          </Group>
          
          {/* Transparent hit area for selection */}
            <Rect
            x={0}
            y={0}
            width={scaledWidth}
            height={scaledHeight}
            fill="transparent"
          />
          
          {/* Selection outline */}
          {isSelected && !isExporting && (
            <Rect
              x={-2}
              y={-2}
              width={scaledWidth + 4}
              height={scaledHeight + 4}
              stroke="#3b82f6"
              strokeWidth={2}
              listening={false}
            />
          )}
        </Group>
      );
    }

    if (element.type === 'guestList') {
      const isSelected = selectedElementIds.includes(element.id);
      const direction = element.guestListDirection || 'across';
      const roomTablesForGuest = getTablesForPlanOrGuestElement(element.guestListRoom);
      const selectedTableIds = resolveGuestListTableIds(element, roomTablesForGuest);
      const hiddenTableSet = new Set(element.guestListHiddenTables || []);
      const showDividers = element.guestListShowDividers ?? true;

      const tableMap = new Map<string, any>();
      roomTablesForGuest.forEach((table) => {
        tableMap.set(table.id, table);
      });

      const guestsByTable: Record<string, { name: string; guests: any[] }> = {};
      const visibleTableIds: string[] = [];
      selectedTableIds.forEach((tableId) => {
        if (hiddenTableSet.has(tableId)) return;
        const table = tableMap.get(tableId);
        if (table) {
          guestsByTable[tableId] = {
            name: table.name,
            guests: [],
          };
          visibleTableIds.push(tableId);
        }
      });

      guests.forEach((guest: any) => {
        for (const tableId of visibleTableIds) {
          const table = tableMap.get(tableId);
          if (!table) continue;

          const seat = table.seating?.find((s: any) => s.rsvpId === guest.id);
          if (seat) {
            guestsByTable[tableId].guests.push({
              ...guest,
              seatNumber: seat.seatNumber ?? null,
              tableName: table.name,
            });
            break;
          }
        }
      });

      const fontSize = (element.guestListFontSize || DEFAULT_GUEST_LIST_FONT_SIZE) * Math.min(scaleX, scaleY);
      const rowHeight = fontSize * 1.5;
      const tableIds = visibleTableIds.filter((id) => guestsByTable[id]);
      // Spacing follows the font size so the layout prints the same whatever the screen size
      const columnSpacing = fontSize * 0.85;
      const tableCount = tableIds.length;

      const sortGuests = (guests: any[]) =>
        [...guests].sort((a: any, b: any) => {
          const seatA = typeof a.seatNumber === 'number' ? a.seatNumber : Number.POSITIVE_INFINITY;
          const seatB = typeof b.seatNumber === 'number' ? b.seatNumber : Number.POSITIVE_INFINITY;
          if (seatA !== seatB) {
            return seatA - seatB;
          }
          return guestDisplayName(a).localeCompare(guestDisplayName(b), 'en-GB', { sensitivity: 'base' });
        });

      const sortedGuestsByTableId: Record<string, any[]> = {};
      const blockHeights: number[] = [];
      /** Vertical layout within each table block (scaled coords): title, column headers, guest rows. */
      const guestListHeaderY = (fontSize * GUEST_LIST_TITLE_SCALE) * 1.35;
      /** Y offset to first guest row; same fixed height used for wrap layout block sizing. */
      const guestListBodyStartY = guestListHeaderY + fontSize * 1.5;
      const blockGap = fontSize * 0.4;

      tableIds.forEach((tid, idx) => {
        const sorted = sortGuests(guestsByTable[tid].guests);
        sortedGuestsByTableId[tid] = sorted;
        const n = sorted.length;
        blockHeights[idx] = guestListBodyStartY + n * rowHeight;
      });

      const wrapConfigured = element.guestListWrapCount;
      const wrapAcross = Math.min(
        Math.max(1, wrapConfigured ?? Math.max(tableCount, 1)),
        Math.max(tableCount, 1)
      );
      const wrapDown = Math.min(
        Math.max(1, wrapConfigured ?? Math.max(tableCount, 1)),
        Math.max(tableCount, 1)
      );

      const rowStartsAcross: number[] = [];
      if (direction === 'across' && tableCount > 0) {
        const rowCount = Math.ceil(tableCount / wrapAcross);
        let yAcc = 0;
        for (let r = 0; r < rowCount; r++) {
          rowStartsAcross[r] = yAcc;
          let rowMax = 0;
          for (let c = 0; c < wrapAcross; c++) {
            const ti = r * wrapAcross + c;
            if (ti >= tableCount) {
              break;
            }
            rowMax = Math.max(rowMax, blockHeights[ti]);
          }
          yAcc += rowMax + blockGap;
        }
      }

      const columnWidthAcross =
        direction === 'across' && tableCount > 0
          ? scaledWidth / wrapAcross
          : scaledWidth / Math.max(tableCount || 1, 1);

      const numColsDown = tableCount > 0 ? Math.ceil(tableCount / wrapDown) : 1;
      const usableWidthDown = scaledWidth - columnSpacing * 2;
      const columnSlotWidthDown = usableWidthDown / Math.max(numColsDown, 1);

      return (
        <Group
          key={element.id}
          {...commonProps}
          x={scaledX}
          y={scaledY}
          rotation={element.rotation}
          opacity={element.opacity}
        >
          {/* Background */}
          <Rect
            x={0}
            y={0}
            width={scaledWidth}
            height={scaledHeight}
            fill="transparent"
            stroke={isSelected && !isExporting ? '#3b82f6' : 'transparent'}
            strokeWidth={isSelected && !isExporting ? 2 : 0}
          />

          {/* Guest list content */}
          <Group>
            {tableIds.map((tableId, tableIndex) => {
              const tableData = guestsByTable[tableId];
              const tableGuests = sortedGuestsByTableId[tableId];

              let posX: number;
              let posY: number;
              let cellContentWidth: number;
              if (direction === 'across') {
                const colInRow = tableIndex % wrapAcross;
                const rowNum = Math.floor(tableIndex / wrapAcross);
                posX = colInRow * columnWidthAcross;
                posY = rowStartsAcross[rowNum] ?? 0;
                cellContentWidth = Math.max(columnWidthAcross - columnSpacing * 2, 0);
              } else {
                const colIndex = Math.floor(tableIndex / wrapDown);
                posX = columnSpacing + colIndex * columnSlotWidthDown;
                let cumulativeHeight = columnSpacing;
                const colStart = colIndex * wrapDown;
                for (let i = colStart; i < tableIndex; i++) {
                  cumulativeHeight += blockHeights[i];
                }
                posY = cumulativeHeight;
                cellContentWidth = Math.max(columnSlotWidthDown - columnSpacing * 2, 0);
              }

              const headerPaddingX = columnSpacing;

              const columnsConfig = [
                { key: 'seat', label: 'Seat', visible: true, getValue: (guest: any) => guest.seatNumber?.toString() || 'N/A' },
                { key: 'name', label: 'Guest', visible: true, getValue: (guest: any) => guestDisplayName(guest) || 'Unknown' },
              ];

              const visibleColumns = columnsConfig.filter(column => column.visible);
              const availableWidth = cellContentWidth;
              const columnWidthForKey = (key: string) => {
                if (key === 'seat') {
                  return availableWidth * 0.2;
                }
                if (key === 'name') {
                  return availableWidth * 0.8;
                }
                return visibleColumns.length > 0 ? availableWidth / visibleColumns.length : availableWidth;
              };
              const columnStartOffsets: number[] = [];
              {
                let acc = 0;
                for (const col of visibleColumns) {
                  columnStartOffsets.push(acc);
                  acc += columnWidthForKey(col.key);
                }
              }
              const columnStartX = posX + headerPaddingX;

              return (
                <Group key={tableId}>
                  <Text
                    x={columnStartX}
                    y={posY}
                    text={tableData.name}
                    fontSize={fontSize * GUEST_LIST_TITLE_SCALE}
                    fontFamily={element.guestListFontFamily || 'Arial'}
                    fontStyle="bold"
                    fill="#000000"
                    listening={false}
                  />

                  {visibleColumns.map((column, columnIndex) => (
                    <Text
                      key={`${tableId}-header-${column.key}`}
                      x={columnStartX + (columnStartOffsets[columnIndex] ?? 0)}
                      y={posY + guestListHeaderY}
                      text={column.label}
                      fontSize={fontSize}
                      fontFamily={element.guestListFontFamily || 'Arial'}
                      fontStyle="bold"
                      fill="#000000"
                      listening={false}
                    />
                  ))}

                  {tableGuests.map((guest: any, guestIndex: number) => (
                    <Group key={guest.id || `${tableId}-guest-${guestIndex}`}>
                      {visibleColumns.map((column, columnIndex) => (
                        <Text
                          key={`${tableId}-guest-${guestIndex}-${column.key}`}
                          x={columnStartX + (columnStartOffsets[columnIndex] ?? 0)}
                          y={posY + guestListBodyStartY + (guestIndex * rowHeight)}
                          text={column.getValue(guest)}
                          fontSize={fontSize * 0.9}
                          fontFamily={element.guestListFontFamily || 'Arial'}
                          fontStyle={element.guestListFontWeight === 'bold' ? 'bold' : 'normal'}
                          fill="#000000"
                          listening={false}
                        />
                      ))}
                    </Group>
                  ))}

                  {direction === 'across' && showDividers && tableIndex < tableIds.length - 1 &&
                    Math.floor(tableIndex / wrapAcross) === Math.floor((tableIndex + 1) / wrapAcross) && (
                    <Line
                      points={[
                        posX + columnWidthAcross,
                        posY,
                        posX + columnWidthAcross,
                        posY + guestListBodyStartY + blockGap + (tableGuests.length * rowHeight)
                      ]}
                      stroke="#d1d5db"
                      strokeWidth={1}
                      listening={false}
                    />
                  )}
                </Group>
              );
            })}
          </Group>

          {isSelected && !isExporting && (
            <Rect
              x={-2}
              y={-2}
              width={scaledWidth + 4}
              height={scaledHeight + 4}
              stroke="#3b82f6"
              strokeWidth={2}
              listening={false}
            />
          )}
        </Group>
      );
    }

    if (element.type === 'guestListSurname') {
      const isSelected = selectedElementIds.includes(element.id);
      const roomTablesForGuest = getTablesForPlanOrGuestElement(element.guestListRoom);
      const selectedTableIds = resolveGuestListTableIds(element, roomTablesForGuest);
      const hiddenTableSet = new Set(element.guestListHiddenTables || []);

      const tableMap = new Map<string, any>();
      roomTablesForGuest.forEach((table) => {
        tableMap.set(table.id, table);
      });

      const visibleTableIds: string[] = [];
      selectedTableIds.forEach((tableId) => {
        if (hiddenTableSet.has(tableId)) {
          return;
        }
        if (tableMap.has(tableId)) {
          visibleTableIds.push(tableId);
        }
      });

      type SurnameRow = {
        displayName: string;
        tableName: string;
        seatLabel: string;
        sortSurname: string;
        sortFirstname: string;
      };

      const collected: Array<{
        surname: string;
        firstname: string;
        row: SurnameRow;
      }> = [];

      visibleTableIds.forEach((tableId) => {
        const table = tableMap.get(tableId);
        if (!table) {
          return;
        }
        for (const seat of table.seating || []) {
          if (!seat.rsvpId) {
            continue;
          }
          const guest = guests.find((g: any) => g.id === seat.rsvpId);
          if (!guest) {
            continue;
          }
          const { surname, firstname } = parseGuestSurnameFirstname(guest);
          collected.push({
            surname,
            firstname,
            row: {
              displayName: formatSurnameCommaFirstname(surname, firstname),
              tableName: table.name,
              seatLabel: seat.seatNumber != null ? String(seat.seatNumber) : 'N/A',
              sortSurname: surname,
              sortFirstname: firstname,
            },
          });
        }
      });

      collected.sort((a, b) =>
        compareSurnameThenFirstname(a.surname, a.firstname, b.surname, b.firstname)
      );

      const groups: { letter: string; rows: SurnameRow[] }[] = [];
      for (const item of collected) {
        const letter = surnameBlockLetter(item.surname);
        const prev = groups[groups.length - 1];
        if (!prev || prev.letter !== letter) {
          groups.push({ letter, rows: [item.row] });
        } else {
          prev.rows.push(item.row);
        }
      }

      for (const g of groups) {
        g.rows.sort((ra, rb) =>
          compareSurnameThenFirstname(ra.sortSurname, ra.sortFirstname, rb.sortSurname, rb.sortFirstname)
        );
      }

      const fontSize = (element.guestListFontSize || DEFAULT_GUEST_LIST_FONT_SIZE) * Math.min(scaleX, scaleY);
      const rowHeight = fontSize * 1.5;
      // Spacing follows the font size so the layout prints the same whatever the screen size
      const columnSpacing = fontSize * 0.85;
      const guestListHeaderY = (fontSize * GUEST_LIST_TITLE_SCALE) * 0.95;
      /** First data row Y: keep close under Name/Table/Seat headings (Konva Text is top-anchored). */
      const guestListBodyStartY = guestListHeaderY + fontSize * 0.85;
      const betweenSectionGap = fontSize * 0.4;

      const numLayoutCols = Math.max(1, element.guestListSurnameColumnCount ?? 1);
      const outerPad = columnSpacing;
      const usableOuter = scaledWidth - outerPad * 2;
      const colGap = numLayoutCols > 1 ? fontSize * 0.25 : 0;
      const slotWidth = (usableOuter - (numLayoutCols - 1) * colGap) / numLayoutCols;
      const innerCellPad = numLayoutCols > 1 ? fontSize * 0.17 : 0;
      const cellContentWidth =
        numLayoutCols <= 1
          ? Math.max(usableOuter, 0)
          : Math.max(slotWidth - innerCellPad * 2, 0);

      const columnWidthForSurnameCol = (key: string) => {
        if (key === 'name') {
          return cellContentWidth * 0.5;
        }
        if (key === 'table') {
          return cellContentWidth * 0.25;
        }
        if (key === 'seat') {
          return cellContentWidth * 0.25;
        }
        return cellContentWidth / 3;
      };

      const columnsConfig = [
        { key: 'name', label: 'Name' },
        { key: 'table', label: 'Table' },
        { key: 'seat', label: 'Seat' },
      ];
      const columnStartOffsets: number[] = [];
      {
        let acc = 0;
        for (const col of columnsConfig) {
          columnStartOffsets.push(acc);
          acc += columnWidthForSurnameCol(col.key);
        }
      }

      const blockHeights = groups.map(
        (g) => guestListBodyStartY + g.rows.length * rowHeight + betweenSectionGap
      );
      const groupCount = groups.length;
      /** Bottom Y bound for starting a new strip: flow fills the element height before using the next column (newspaper-style). */
      let maxColumnBottom = Math.max(0, scaledHeight - outerPad);
      if (maxColumnBottom < 1) {
        maxColumnBottom = Number.POSITIVE_INFINITY;
      }
      const baseYByGi: number[] = new Array(groupCount);
      const colIndexByGi: number[] = new Array(groupCount);
      const yCursorPerCol = new Array(numLayoutCols).fill(0);
      /** Column pointer only ever moves forward, so alphabetical order is preserved across strips. */
      let currentCol = 0;
      for (let gi = 0; gi < groupCount; gi++) {
        const h = blockHeights[gi] ?? 0;
        while (currentCol < numLayoutCols - 1 && yCursorPerCol[currentCol] + h > maxColumnBottom) {
          currentCol += 1;
        }
        colIndexByGi[gi] = currentCol;
        baseYByGi[gi] = yCursorPerCol[currentCol] ?? 0;
        yCursorPerCol[currentCol] += h;
      }

      return (
        <Group
          key={element.id}
          {...commonProps}
          x={scaledX}
          y={scaledY}
          rotation={element.rotation}
          opacity={element.opacity}
        >
          <Rect
            x={0}
            y={0}
            width={scaledWidth}
            height={scaledHeight}
            fill="transparent"
            stroke={isSelected && !isExporting ? '#3b82f6' : 'transparent'}
            strokeWidth={isSelected && !isExporting ? 2 : 0}
          />

          <Group>
            {groups.length === 0 ? (
              <Text
                x={scaledWidth / 2}
                y={scaledHeight / 2}
                text="No seated guests in selected tables"
                fontSize={16}
                fill="#6b7280"
                align="center"
                verticalAlign="middle"
                offsetX={140}
                offsetY={8}
                listening={false}
              />
            ) : (
              groups.map((g, gi) => {
                /** Strips fill top-to-bottom until the box height is used, then continue in the next strip to the right. */
                const col = colIndexByGi[gi] ?? 0;
                const baseX = outerPad + col * (slotWidth + colGap);
                const baseY = baseYByGi[gi] ?? 0;
                const textOriginX = baseX + innerCellPad;
                return (
                  <Group key={`${element.id}-surname-${gi}-${g.letter}`}>
                    <Text
                      x={textOriginX}
                      y={baseY}
                      text={g.letter}
                      fontSize={fontSize * GUEST_LIST_TITLE_SCALE}
                      fontFamily={element.guestListFontFamily || 'Arial'}
                      fontStyle="bold"
                      fill="#000000"
                      listening={false}
                    />
                    {columnsConfig.map((colDef, columnIndex) => (
                      <Text
                        key={`${colDef.key}-header`}
                        x={textOriginX + (columnStartOffsets[columnIndex] ?? 0)}
                        y={baseY + guestListHeaderY}
                        text={colDef.label}
                        fontSize={fontSize}
                        fontFamily={element.guestListFontFamily || 'Arial'}
                        fontStyle="bold"
                        fill="#000000"
                        listening={false}
                      />
                    ))}
                    {g.rows.map((dataRow, rowIndex) => (
                      <Group key={`row-${gi}-${rowIndex}`}>
                        <Text
                          x={textOriginX + (columnStartOffsets[0] ?? 0)}
                          y={baseY + guestListBodyStartY + rowIndex * rowHeight}
                          text={dataRow.displayName}
                          fontSize={fontSize * 0.9}
                          fontFamily={element.guestListFontFamily || 'Arial'}
                          fontStyle={element.guestListFontWeight === 'bold' ? 'bold' : 'normal'}
                          fill="#000000"
                          listening={false}
                        />
                        <Text
                          x={textOriginX + (columnStartOffsets[1] ?? 0)}
                          y={baseY + guestListBodyStartY + rowIndex * rowHeight}
                          text={dataRow.tableName}
                          fontSize={fontSize * 0.9}
                          fontFamily={element.guestListFontFamily || 'Arial'}
                          fontStyle={element.guestListFontWeight === 'bold' ? 'bold' : 'normal'}
                          fill="#000000"
                          listening={false}
                        />
                        <Text
                          x={textOriginX + (columnStartOffsets[2] ?? 0)}
                          y={baseY + guestListBodyStartY + rowIndex * rowHeight}
                          text={dataRow.seatLabel}
                          fontSize={fontSize * 0.9}
                          fontFamily={element.guestListFontFamily || 'Arial'}
                          fontStyle={element.guestListFontWeight === 'bold' ? 'bold' : 'normal'}
                          fill="#000000"
                          listening={false}
                        />
                      </Group>
                    ))}
                  </Group>
                );
              })
            )}
          </Group>

          {isSelected && !isExporting && (
            <Rect
              x={-2}
              y={-2}
              width={scaledWidth + 4}
              height={scaledHeight + 4}
              stroke="#3b82f6"
              strokeWidth={2}
              listening={false}
            />
          )}
        </Group>
      );
    }

    if (element.type === 'shape') {
      const isSelected = selectedElementIds.includes(element.id);
      
      if (element.shapeType === 'rectangle') {
        return (
          <Group 
            key={element.id} 
            {...commonProps}
            x={scaledX}
            y={scaledY}
            rotation={element.rotation}
            opacity={element.opacity}
          >
            <Rect
              x={0}
              y={0}
              width={scaledWidth}
              height={scaledHeight}
              fill={element.fillColor || '#3b82f6'}
              stroke={element.strokeColor || '#1e3a8a'}
              strokeWidth={element.strokeWidth || 2}
            />
            
            {isSelected && !isExporting && (
              <Rect
                x={-3}
                y={-3}
                width={scaledWidth + 6}
                height={scaledHeight + 6}
                stroke="#3b82f6"
                strokeWidth={2}
                listening={false}
                dash={[4, 4]}
              />
            )}
          </Group>
        );
      }

      if (element.shapeType === 'circle') {
        return (
          <Group 
            key={element.id} 
            {...commonProps}
            x={scaledX}
            y={scaledY}
            rotation={element.rotation}
            opacity={element.opacity}
          >
            <Circle
              x={scaledWidth / 2}
              y={scaledHeight / 2}
              radius={Math.min(scaledWidth, scaledHeight) / 2}
              fill={element.fillColor || '#3b82f6'}
              stroke={element.strokeColor || '#1e3a8a'}
              strokeWidth={element.strokeWidth || 2}
            />
            
            {isSelected && !isExporting && (
              <Circle
                x={scaledWidth / 2}
                y={scaledHeight / 2}
                radius={Math.min(scaledWidth, scaledHeight) / 2 + 4}
                stroke="#3b82f6"
                strokeWidth={2}
                listening={false}
                dash={[4, 4]}
              />
            )}
          </Group>
        );
      }

      if (element.shapeType === 'line') {
        return (
          <Group 
            key={element.id} 
            {...commonProps}
            x={scaledX}
            y={scaledY}
            rotation={element.rotation}
            opacity={element.opacity}
          >
            {/* Hit area - transparent rect for line selection */}
            <Rect
              x={-10}
              y={-10}
              width={scaledWidth + 20}
              height={scaledHeight + 20}
              fill="transparent"
            />
            
            <Line
              points={[0, 0, scaledWidth, scaledHeight]}
              stroke={element.strokeColor || '#1e3a8a'}
              strokeWidth={element.strokeWidth || 2}
              listening={false}
            />
            
            {isSelected && !isExporting && (
              <Line
                points={[0, 0, scaledWidth, scaledHeight]}
                stroke="#3b82f6"
                strokeWidth={(element.strokeWidth || 2) + 4}
                listening={false}
                opacity={0.3}
              />
            )}
          </Group>
        );
      }
    }

    return null;
  };

  const selectedElement = elements.find(el => el.id === selectedElementIds[0]);

  /** Page units (mm) to layer pixels, as used by renderElement. */
  const designerPxPerMm = Math.min(
    previewDimensions.width / dimensions.width,
    previewDimensions.height / dimensions.height
  );

  /** Scale/pan (layer pixels) the table plan is currently drawn with, resolving fit mode against its frame. */
  const getTablePlanView = (element: CanvasElement): TablePlanView =>
    resolveTablePlanView(
      element,
      getTablesForPlanOrGuestElement(element.tablePlanRoom),
      Math.round(element.width * (previewDimensions.width / dimensions.width)),
      Math.round(element.height * (previewDimensions.height / dimensions.height)),
      designerPxPerMm
    );

  /**
   * Manual zoom/pan (values in layer pixels, as shown in the sidebar): leaves fit mode, starting from
   * what is currently shown so the plan does not jump, and stores the result in page units.
   */
  const updateTablePlanView = (element: CanvasElement, updates: Partial<TablePlanView>) => {
    const view = { ...getTablePlanView(element), ...updates };
    updateElement(element.id, {
      tablePlanFit: false,
      tablePlanUnits: 'page',
      tablePlanScale: view.scale / designerPxPerMm,
      tablePlanX: view.panX / designerPxPerMm,
      tablePlanY: view.panY / designerPxPerMm,
    });
  };

  const selectedTablePlanView = selectedElement?.type === 'tablePlan' ? getTablePlanView(selectedElement) : null;

  const guestListEditorTables =
    selectedElement?.type === 'guestList' || selectedElement?.type === 'guestListSurname'
      ? getTablesForPlanOrGuestElement(selectedElement.guestListRoom)
      : tables;
  const guestListEditorRoomValue =
    selectedElement?.type === 'guestList' || selectedElement?.type === 'guestListSurname'
      ? resolveElementRoomId(selectedElement.guestListRoom) || ''
      : '';

  return (
    <div 
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden'
      }}
    >
      <div className="bg-white rounded-lg w-full max-w-[95vw] h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold">Floor Plan Designer</h2>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={undo}
                disabled={historyPosition.index <= 0}
                className="text-white hover:bg-blue-800"
              >
                <Undo className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={redo}
                disabled={historyPosition.index >= historyPosition.length - 1}
                className="text-white hover:bg-blue-800"
              >
                <Redo className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm opacity-90">
              {paperSize} {orientation} • Zoom: {Math.round(zoom * 100)}%
            </span>
            {/* Auto-save status indicator */}
            <span className="text-xs px-2 py-1 rounded flex items-center gap-1">
              {saveStatus === 'saving' && (
                <>
                  <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Saving...</span>
                </>
              )}
              {saveStatus === 'saved' && (
                <>
                  <Save className="h-3 w-3 text-green-300" />
                  <span className="text-green-300">Saved</span>
                </>
              )}
              {saveStatus === 'unsaved' && <span className="opacity-90">Unsaved changes</span>}
              {saveStatus === 'error' && (
                <button
                  type="button"
                  className="rounded bg-red-600 px-2 py-0.5 font-medium text-white hover:bg-red-700"
                  title={saveError ?? undefined}
                  onClick={() => {
                    autoSaveFloorPlan().catch(() => {
                      /* status stays as error */
                    });
                  }}
                >
                  Not saved – retry
                </button>
              )}
              {isUploadingImage && <span className="opacity-90">Uploading image…</span>}
            </span>
            <Button variant="ghost" size="icon" onClick={() => void handleClose()} className="text-white hover:bg-blue-800">
            <X className="h-5 w-5" />
          </Button>
          </div>
        </div>

        {!isLoading && (
          <div className="border-b bg-white px-4 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground shrink-0">
                Design plans
              </span>
              <div
                className="flex flex-wrap items-center gap-2 min-w-0 flex-1"
                role="tablist"
                aria-label="Design plans"
              >
                {floorPlanTabRoomIds.map((rid) => {
                  const isActive = rid === activeFloorPlanRoomId;
                  const isPersistedDesign = designPlans.some((d) => d.id === rid);
                  return (
                    <div
                      key={rid}
                      className={`flex items-center gap-0.5 shrink-0 rounded-md border bg-background pl-2 pr-0.5 py-0.5 shadow-sm ${
                        isActive ? 'ring-2 ring-primary border-primary' : 'border-border'
                      }`}
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={`max-w-[200px] truncate px-1.5 py-1 text-sm font-medium rounded hover:bg-muted/80 inline-flex items-center gap-1.5 ${
                          isActive ? 'text-primary' : 'text-foreground'
                        }`}
                        onClick={() => switchFloorPlanRoom(rid)}
                        title={floorPlanRoomTabLabel(rid)}
                      >
                        <Building2 className="h-3.5 w-3.5 shrink-0 opacity-80" />
                        <span className="truncate">{floorPlanRoomTabLabel(rid)}</span>
                      </button>
                      {isPersistedDesign && unitId && eventId && (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRenameDesignTarget({
                                id: rid,
                                name: designPlans.find((d) => d.id === rid)?.name ?? '',
                              });
                            }}
                            title="Rename floor plan"
                            aria-label={`Rename ${floorPlanRoomTabLabel(rid)}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              void deleteFloorPlanDesign(rid);
                            }}
                            title="Remove floor plan"
                            aria-label={`Remove ${floorPlanRoomTabLabel(rid)}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })}
                {unitId && eventId && (
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="h-8 w-8 shrink-0"
                    onClick={() => setShowCreateDesignModal(true)}
                    title="Add design plan"
                    aria-label="Add design plan"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar */}
          <div className="w-80 border-r overflow-y-auto bg-gray-50">
            <Tabs defaultValue="elements" className="w-full">
              <TabsList className="w-full grid grid-cols-3">
                <TabsTrigger value="elements">Add</TabsTrigger>
                <TabsTrigger value="properties">Edit</TabsTrigger>
                <TabsTrigger value="layers">Layers</TabsTrigger>
              </TabsList>

              <TabsContent value="elements" className="p-4 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Paper Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                      <Label className="text-xs">Size</Label>
                  <Select value={paperSize} onValueChange={(v) => changePageSetup(v as PaperSize, orientation)}>
                        <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                          <SelectItem value="A1">A1 - Large Poster</SelectItem>
                          <SelectItem value="A2">A2 - Medium Poster</SelectItem>
                          <SelectItem value="A3">A3 - Tabloid</SelectItem>
                          <SelectItem value="A4">A4 - Letter</SelectItem>
                          <SelectItem value="A5">A5 - Half Letter</SelectItem>
                          <SelectItem value="A6">A6 - Postcard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">Orientation</Label>
                  <Select value={orientation} onValueChange={(v) => changePageSetup(paperSize, v as Orientation)}>
                        <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portrait">Portrait</SelectItem>
                      <SelectItem value="landscape">Landscape</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                      <Label className="text-xs">Frame</Label>
                  <Select value={frameStyle} onValueChange={(v) => setFrameStyle(v as FrameStyle)}>
                        <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="simple">Simple</SelectItem>
                      <SelectItem value="elegant">Elegant</SelectItem>
                      <SelectItem value="decorative">Decorative</SelectItem>
                          <SelectItem value="masonic">Masonic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Add Elements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => addElement('text')}
                >
                  <Type className="h-4 w-4 mr-2" />
                  Text Box
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                      onClick={() => {
                        fileInputRef.current?.click();
                      }}
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                      Upload Image
                </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => addElement('unitLogo')}
                  disabled={!unitLogoUrl}
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  Logo
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => addElement('tablePlan')}
                >
                  <LayoutGrid className="h-4 w-4 mr-2" />
                      Table Layout
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => addElement('guestList')}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Guest List (Table)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => addElement('guestListSurname')}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Guest List (Surname)
                </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => addElement('shape')}
                    >
                      <LayoutGrid className="h-4 w-4 mr-2" />
                      Shape
                </Button>
              </CardContent>
            </Card>

              <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm">View Options</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Show Grid</Label>
                      <input
                        type="checkbox"
                        checked={showGrid}
                        onChange={(e) => setShowGrid(e.target.checked)}
                        className="rounded"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Snap to Grid</Label>
                      <input
                        type="checkbox"
                        checked={snapToGrid}
                        onChange={(e) => setSnapToGrid(e.target.checked)}
                        className="rounded"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Zoom</Label>
                      <div className="flex gap-2 mt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => zoomTo(zoom / DESIGNER_ZOOM_STEP)}
                        >
                          <ZoomOut className="h-3 w-3" />
                        </Button>
                        <span className="flex-1 text-center text-sm py-1">
                          {Math.round(zoom * 100)}%
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => zoomTo(zoom * DESIGNER_ZOOM_STEP)}
                        >
                          <ZoomIn className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setZoom(1);
                            setPanX(0);
                            setPanY(0);
                          }}
                          className="ml-2"
                        >
                          Reset
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="properties" className="p-4 space-y-4">
                {selectedElement ? (
                  <>
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">Element: {selectedElement.name}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <Label className="text-xs">Name</Label>
                          <Input
                            value={selectedElement.name || ''}
                            onChange={(e) => updateElement(selectedElement.id, { name: e.target.value })}
                            className="h-8"
                          />
                        </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                            <Label className="text-xs">X</Label>
                      <Input
                        type="number"
                        value={Math.round(selectedElement.x)}
                              onChange={(e) => updateElement(selectedElement.id, { x: parseInt(e.target.value) || 0 })}
                        className="h-8"
                      />
                    </div>
                    <div>
                            <Label className="text-xs">Y</Label>
                      <Input
                        type="number"
                        value={Math.round(selectedElement.y)}
                              onChange={(e) => updateElement(selectedElement.id, { y: parseInt(e.target.value) || 0 })}
                        className="h-8"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Width</Label>
                      <Input
                        type="number"
                        value={Math.round(selectedElement.width)}
                              onChange={(e) => updateElement(selectedElement.id, { width: parseInt(e.target.value) || 50 })}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Height</Label>
                      <Input
                        type="number"
                        value={Math.round(selectedElement.height)}
                              onChange={(e) => updateElement(selectedElement.id, { height: parseInt(e.target.value) || 50 })}
                        className="h-8"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Position</Label>
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          updateElement(selectedElement.id, {
                            x: Math.max(0, Math.round((dimensions.width - selectedElement.width) / 2)),
                          })
                        }
                        title="Center Horizontally"
                      >
                        <AlignHorizontalJustifyCenter className="h-4 w-4 mr-1.5" />
                        Center H
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          updateElement(selectedElement.id, {
                            y: Math.max(0, Math.round((dimensions.height - selectedElement.height) / 2)),
                          })
                        }
                        title="Center Vertically"
                      >
                        <AlignVerticalJustifyCenter className="h-4 w-4 mr-1.5" />
                        Center V
                      </Button>
                    </div>
                  </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Rotation</Label>
                            <Input
                              type="number"
                              value={Math.round(selectedElement.rotation)}
                              onChange={(e) => updateElement(selectedElement.id, { rotation: parseInt(e.target.value) || 0 })}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Opacity</Label>
                            <Slider
                              value={[selectedElement.opacity * 100]}
                              onValueChange={(values: number[]) => updateElement(selectedElement.id, { opacity: values[0] / 100 })}
                              max={100}
                              step={5}
                              className="mt-2"
                      />
                    </div>
                  </div>

                  {selectedElement.type === 'text' && (
                    <>
                      <div>
                              <Label className="text-xs">Text</Label>
                        <Input
                          value={selectedElement.content || ''}
                                onChange={(e) => updateElement(selectedElement.id, { content: e.target.value })}
                          className="h-8"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Font Size</Label>
                          <div className="space-y-2">
                          <Input
                            type="number"
                            value={selectedElement.fontSize || 16}
                              onChange={(e) => updateElement(selectedElement.id, { fontSize: parseInt(e.target.value) || 16 })}
                            className="h-8"
                              min="1"
                              max="200"
                            />
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { fontSize: Math.max(1, (selectedElement.fontSize || 16) - 2) })}
                              >
                                A-
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { fontSize: Math.min(200, (selectedElement.fontSize || 16) + 2) })}
                              >
                                A+
                              </Button>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { fontSize: 1 })}
                              >
                                1
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { fontSize: 4 })}
                              >
                                4
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { fontSize: 8 })}
                              >
                                8
                              </Button>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { fontSize: 12 })}
                              >
                                12
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { fontSize: 16 })}
                              >
                                16
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { fontSize: 24 })}
                              >
                                24
                              </Button>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { fontSize: 32 })}
                              >
                                32
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { fontSize: 48 })}
                              >
                                48
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { fontSize: 72 })}
                              >
                                72
                              </Button>
                            </div>
                          </div>
                        </div>
                        <div>
                                <Label className="text-xs">Font</Label>
                          <Select 
                            value={selectedElement.fontFamily || 'Arial'} 
                            onValueChange={(value) => updateElement(selectedElement.id, { fontFamily: value })}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <div className="px-2 py-1 text-[11px] uppercase tracking-wide text-gray-500">Sans-serif</div>
                              <SelectItem value="Arial">Arial</SelectItem>
                              <SelectItem value="Helvetica">Helvetica</SelectItem>
                              <SelectItem value="Verdana">Verdana</SelectItem>
                              <SelectItem value="Tahoma">Tahoma</SelectItem>
                              <SelectItem value="Trebuchet MS">Trebuchet MS</SelectItem>
                              <SelectItem value="Century Gothic">Century Gothic</SelectItem>
                              <SelectItem value="Lucida Sans">Lucida Sans</SelectItem>
                              <SelectItem value="Futura">Futura</SelectItem>
                              <SelectItem value="Roboto">Roboto</SelectItem>
                              <SelectItem value="Open Sans">Open Sans</SelectItem>
                              
                              <div className="px-2 py-1 mt-2 text-[11px] uppercase tracking-wide text-gray-500">Serif</div>
                              <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                              <SelectItem value="Georgia">Georgia</SelectItem>
                              <SelectItem value="Baskerville">Baskerville</SelectItem>
                              <SelectItem value="Garamond">Garamond</SelectItem>
                              <SelectItem value="Warnock Pro">Warnock Pro</SelectItem>
                              <SelectItem value="Palatino">Palatino</SelectItem>
                              
                              <div className="px-2 py-1 mt-2 text-[11px] uppercase tracking-wide text-gray-500">Monospace</div>
                              <SelectItem value="Courier New">Courier New</SelectItem>
                              <SelectItem value="Monaco">Monaco</SelectItem>
                              <SelectItem value="Consolas">Consolas</SelectItem>
                              <SelectItem value="Source Code Pro">Source Code Pro</SelectItem>
                              
                              <div className="px-2 py-1 mt-2 text-[11px] uppercase tracking-wide text-gray-500">Display</div>
                              <SelectItem value="Impact">Impact</SelectItem>
                              <SelectItem value="Copperplate">Copperplate</SelectItem>
                              <SelectItem value="Oswald">Oswald</SelectItem>
                              
                              <div className="px-2 py-1 mt-2 text-[11px] uppercase tracking-wide text-gray-500">Handwriting & Script</div>
                              <SelectItem value="Brush Script MT">Brush Script MT</SelectItem>
                              <SelectItem value="Great Vibes">Great Vibes</SelectItem>
                              <SelectItem value="Dancing Script">Dancing Script</SelectItem>
                              <SelectItem value="Parisienne">Parisienne</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                                <Label className="text-xs">Text Color</Label>
                                <Input
                                  type="color"
                                  value={selectedElement.textColor || '#000000'}
                                  onChange={(e) => updateElement(selectedElement.id, { textColor: e.target.value })}
                                  className="h-8"
                                />
                        </div>
                        <div>
                                <Label className="text-xs">Background</Label>
                                <Input
                                  type="color"
                                  value={selectedElement.backgroundColor === 'transparent' ? '#ffffff' : selectedElement.backgroundColor || '#ffffff'}
                                  onChange={(e) => updateElement(selectedElement.id, { backgroundColor: e.target.value })}
                                  className="h-8"
                                />
                        </div>
                      </div>

                            <div className="grid grid-cols-3 gap-2">
                              <Button
                                variant={selectedElement.fontWeight === 'bold' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => updateElement(selectedElement.id, { fontWeight: selectedElement.fontWeight === 'bold' ? 'normal' : 'bold' })}
                              >
                                <strong>B</strong>
                              </Button>
                              <Button
                                variant={selectedElement.fontStyle === 'italic' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => updateElement(selectedElement.id, { fontStyle: selectedElement.fontStyle === 'italic' ? 'normal' : 'italic' })}
                              >
                                <em>I</em>
                              </Button>
                              <Button
                                variant={selectedElement.textDecoration === 'underline' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => updateElement(selectedElement.id, { textDecoration: selectedElement.textDecoration === 'underline' ? 'none' : 'underline' })}
                              >
                                <u>U</u>
                              </Button>
                        </div>
                    </>
                  )}

                        {selectedElement.type === 'tablePlan' && (
                          <>
                            <div className="space-y-3 p-3 border rounded-lg bg-gray-50">
                              <div className="text-xs font-semibold text-gray-700 mb-2">Table Plan Viewport</div>
                              
                              {/* Room Selection */}
                              {rooms.length > 0 && (
                        <div>
                                  <Label className="text-xs">Room</Label>
                          <Select 
                                    value={
                                      resolveElementRoomId(selectedElement.tablePlanRoom) || ''
                                    }
                                    onValueChange={(value) => updateElement(selectedElement.id, { tablePlanRoom: value })}
                          >
                            <SelectTrigger className="h-8">
                                      <SelectValue placeholder="Select room" />
                            </SelectTrigger>
                            <SelectContent>
                                      {rooms.map((room) => (
                                        <SelectItem key={room.id} value={room.id}>
                                          {room.name}
                                        </SelectItem>
                                      ))}
                            </SelectContent>
                          </Select>
                        </div>
                              )}
                              
                              <div>
                                <Label className="text-xs">Zoom</Label>
                                <div className="flex gap-2 mb-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-xs flex-1"
                                    onClick={() => {
                                      const currentScale = selectedTablePlanView?.scale ?? 1;
                                      const next = Math.max(0.01, Math.round((currentScale - 0.01) * 100) / 100);
                                      updateTablePlanView(selectedElement, { scale: next });
                                    }}
                                  >
                                    Zoom Out
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-xs flex-1"
                                    onClick={() => {
                                      const currentScale = selectedTablePlanView?.scale ?? 1;
                                      const next = Math.min(20, Math.round((currentScale + 0.01) * 100) / 100);
                                      updateTablePlanView(selectedElement, { scale: next });
                                    }}
                                  >
                                    Zoom In
                                  </Button>
                                </div>
                                <Slider
                                  value={[(selectedTablePlanView?.scale ?? 1) * 100]}
                                  onValueChange={(values: number[]) => updateTablePlanView(selectedElement, { scale: values[0] / 100 })}
                                  min={1}
                                  max={300}
                                  step={1}
                                  className="mt-2"
                                />
                                <div className="flex justify-between items-center mt-1">
                                  <p className="text-xs text-gray-500">
                                    {Math.round((selectedTablePlanView?.scale ?? 1) * 100)}%{selectedElement.tablePlanFit ? ' (fitted)' : ''}
                                  </p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-xs"
                                    onClick={() => {
                                      // Reset to default scale (100%)
                                      updateTablePlanView(selectedElement, { scale: 1 });
                                    }}
                                  >
                                    Reset
                                  </Button>
                        </div>
                      </div>

                        <div>
                                <Label className="text-xs">Pan Horizontal</Label>
                                <div className="flex gap-2 mb-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-xs flex-1"
                                    onClick={() => {
                                      const currentX = selectedTablePlanView?.panX ?? 0;
                                      updateTablePlanView(selectedElement, { panX: currentX - 1 });
                                    }}
                                  >
                                    ← Left
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-xs flex-1"
                                    onClick={() => {
                                      const currentX = selectedTablePlanView?.panX ?? 0;
                                      updateTablePlanView(selectedElement, { panX: currentX + 1 });
                                    }}
                                  >
                                    Right →
                                  </Button>
                        </div>
                                <Slider
                                  value={[selectedTablePlanView?.panX ?? 0]}
                                  onValueChange={(values: number[]) => updateTablePlanView(selectedElement, { panX: values[0] })}
                                  min={-1000}
                                  max={1000}
                                  step={1}
                                  className="mt-2"
                                />
                                <div className="flex justify-between items-center mt-1">
                                  <p className="text-xs text-gray-500">
                                    {Math.round(selectedTablePlanView?.panX ?? 0)}px
                                  </p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-xs"
                                    onClick={() => updateTablePlanView(selectedElement, { panX: 0 })}
                                  >
                                    Center
                                  </Button>
                                </div>
                              </div>

                        <div>
                                <Label className="text-xs">Pan Vertical</Label>
                                <div className="flex gap-2 mb-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-xs flex-1"
                                    onClick={() => {
                                      const currentY = selectedTablePlanView?.panY ?? 0;
                                      updateTablePlanView(selectedElement, { panY: currentY - 1 });
                                    }}
                                  >
                                    ↑ Up
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-xs flex-1"
                                    onClick={() => {
                                      const currentY = selectedTablePlanView?.panY ?? 0;
                                      updateTablePlanView(selectedElement, { panY: currentY + 1 });
                                    }}
                                  >
                                    Down ↓
                                  </Button>
                                </div>
                                <Slider
                                  value={[selectedTablePlanView?.panY ?? 0]}
                                  onValueChange={(values: number[]) => updateTablePlanView(selectedElement, { panY: values[0] })}
                                  min={-1000}
                                  max={1000}
                                  step={1}
                                  className="mt-2"
                                />
                                <div className="flex justify-between items-center mt-1">
                                  <p className="text-xs text-gray-500">
                                    {Math.round(selectedTablePlanView?.panY ?? 0)}px
                                  </p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-xs"
                                    onClick={() => updateTablePlanView(selectedElement, { panY: 0 })}
                                  >
                                    Center
                                  </Button>
                                </div>
                              </div>

                              <div className="flex gap-2 pt-2 border-t border-gray-200">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 text-xs"
                                  onClick={() => updateTablePlanView(selectedElement, { scale: 1, panX: 0, panY: 0 })}
                                >
                                  Reset All
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 text-xs"
                                  onClick={() => {
                                    // Size the whole plan to the element bounds (kept fitted on resize)
                                    updateElement(selectedElement.id, { tablePlanFit: true });
                                  }}
                                >
                                  Fit to Bounds
                                </Button>
                              </div>
                              
                              <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                                💡 <strong>Quick Tips:</strong><br/>
                                • Use arrow buttons for precise positioning<br/>
                                • Zoom In/Out for quick adjustments<br/>
                                • Fit to Bounds for optimal starting size
                        </div>
                      </div>
                    </>
                  )}

                  {(selectedElement.type === 'guestList' ||
                    selectedElement.type === 'guestListSurname') && (
                    <>
                      {rooms.length > 0 && (
                        <div className="mb-3">
                          <Label className="text-xs">Room</Label>
                          <Select
                            value={guestListEditorRoomValue || rooms[0]?.id || ''}
                            onValueChange={(roomId) => {
                              updateElement(selectedElement.id, {
                                guestListRoom: roomId,
                                guestListSelectedTables: undefined,
                                guestListHiddenTables: [],
                              });
                            }}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue placeholder="Select room" />
                            </SelectTrigger>
                            <SelectContent>
                              {rooms.map((room) => (
                                <SelectItem key={room.id} value={room.id}>
                                  {room.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-[11px] text-muted-foreground mt-1">
                            Tables listed below are for this room. Defaults follow the designer tab you have open.
                          </p>
                        </div>
                      )}

                        <div>
                          <Label className="text-xs">Font Size</Label>
                          <div className="space-y-2">
                          <Input
                            type="number"
                              value={selectedElement.guestListFontSize || DEFAULT_GUEST_LIST_FONT_SIZE}
                              onChange={(e) => updateElement(selectedElement.id, { guestListFontSize: parseInt(e.target.value) || DEFAULT_GUEST_LIST_FONT_SIZE })}
                            className="h-8"
                              min="1"
                              max="100"
                            />
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { guestListFontSize: Math.max(1, (selectedElement.guestListFontSize || DEFAULT_GUEST_LIST_FONT_SIZE) - 1) })}
                              >
                                A-
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { guestListFontSize: Math.min(100, (selectedElement.guestListFontSize || DEFAULT_GUEST_LIST_FONT_SIZE) + 1) })}
                              >
                                A+
                              </Button>
                        </div>
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { guestListFontSize: 1 })}
                              >
                                1
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { guestListFontSize: 4 })}
                              >
                                4
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { guestListFontSize: 6 })}
                              >
                                6
                              </Button>
                        </div>
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { guestListFontSize: 8 })}
                              >
                                8
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { guestListFontSize: 10 })}
                              >
                                10
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { guestListFontSize: 12 })}
                              >
                                12
                              </Button>
                      </div>
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { guestListFontSize: 14 })}
                              >
                                14
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { guestListFontSize: 16 })}
                              >
                                16
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={() => updateElement(selectedElement.id, { guestListFontSize: 20 })}
                              >
                                20
                              </Button>
                            </div>
                        </div>
                      </div>

                      {selectedElement.type === 'guestList' && (
                        <>
                      <div>
                        <Label className="text-xs">Layout Direction</Label>
                        <Select 
                          value={selectedElement.guestListDirection || 'across'}
                          onValueChange={(value) => updateElement(selectedElement.id, { guestListDirection: value as 'across' | 'down' })}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="across">Across (Columns)</SelectItem>
                            <SelectItem value="down">Down (Rows)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs">
                          {selectedElement.guestListDirection === 'down'
                            ? 'Rows per column'
                            : 'Columns per row'}
                        </Label>
                        <Input
                          type="number"
                          min={1}
                          className="h-8"
                          placeholder="All in one line"
                          value={
                            selectedElement.guestListWrapCount === undefined
                              ? ''
                              : String(selectedElement.guestListWrapCount)
                          }
                          onChange={(e) => {
                            const raw = e.target.value.trim();
                            if (raw === '') {
                              updateElement(selectedElement.id, { guestListWrapCount: undefined });
                              return;
                            }
                            const n = parseInt(raw, 10);
                            if (!Number.isFinite(n) || n < 1) {
                              return;
                            }
                            updateElement(selectedElement.id, { guestListWrapCount: n });
                          }}
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Leave blank for a single row (across) or column (down). Set a number to wrap after
                          that many tables.
                        </p>
                      </div>
                        </>
                      )}

                      {(() => {
                        const orderedIds = resolveGuestListTableIds(selectedElement, guestListEditorTables);
                        const tableById = new Map(guestListEditorTables.map((t) => [t.id, t]));
                        const orderedTables = orderedIds.map((id) => tableById.get(id)).filter(Boolean) as any[];
                        const otherTables = guestListEditorTables.filter((t) => !orderedIds.includes(t.id));
                        const setOrder = (ids: string[]) =>
                          updateElement(selectedElement.id, { guestListSelectedTables: ids });

                        return (
                          <div>
                            <Label className="text-xs">Table Order</Label>
                            <div className="space-y-2 mt-2 max-h-56 overflow-y-auto">
                              {orderedTables.length === 0 && (
                                <p className="text-xs text-muted-foreground">No tables included. Add one below.</p>
                              )}
                              {orderedTables.map((table, tableIndex) => {
                                const tableId = table.id;
                                const hidden = selectedElement.guestListHiddenTables?.includes(tableId);

                                return (
                                  <div
                                    key={tableId}
                                    className="flex items-center justify-between rounded border border-gray-200 bg-white px-2 py-1"
                                  >
                                    <div className="flex items-center gap-2 min-w-0">
                                      <button
                                        type="button"
                                        className={`h-6 w-6 shrink-0 rounded border flex items-center justify-center ${hidden ? 'text-gray-400' : 'text-gray-600'}`}
                                        onClick={() => {
                                          const currentHidden = selectedElement.guestListHiddenTables || [];
                                          const nextHidden = hidden
                                            ? currentHidden.filter(id => id !== tableId)
                                            : [...new Set([...currentHidden, tableId])];
                                          updateElement(selectedElement.id, { guestListHiddenTables: nextHidden });
                                        }}
                                        title={hidden ? 'Show table' : 'Hide table'}
                                      >
                                        {hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                      </button>
                                      <span className="text-xs font-medium truncate">{table.name}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 p-0"
                                        onClick={() => {
                                          const newOrder = [...orderedIds];
                                          [newOrder[tableIndex - 1], newOrder[tableIndex]] = [newOrder[tableIndex], newOrder[tableIndex - 1]];
                                          setOrder(newOrder);
                                        }}
                                        disabled={tableIndex <= 0}
                                        title="Move up"
                                      >
                                        <ChevronUp className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 p-0"
                                        onClick={() => {
                                          const newOrder = [...orderedIds];
                                          [newOrder[tableIndex + 1], newOrder[tableIndex]] = [newOrder[tableIndex], newOrder[tableIndex + 1]];
                                          setOrder(newOrder);
                                        }}
                                        disabled={tableIndex >= orderedIds.length - 1}
                                        title="Move down"
                                      >
                                        <ChevronDown className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                                        onClick={() => setOrder(orderedIds.filter((id) => id !== tableId))}
                                        title="Remove table"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {otherTables.length > 0 && (
                              <div className="mt-3">
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs">Not included</Label>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => setOrder([...orderedIds, ...otherTables.map((t) => t.id)])}
                                  >
                                    Add all
                                  </Button>
                                </div>
                                <div className="space-y-1 mt-1 max-h-40 overflow-y-auto">
                                  {otherTables.map((table) => (
                                    <div
                                      key={table.id}
                                      className="flex items-center justify-between rounded border border-dashed border-gray-300 bg-white px-2 py-1"
                                    >
                                      <span className="text-xs text-muted-foreground truncate">{table.name}</span>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 p-0"
                                        onClick={() => setOrder([...orderedIds, table.id])}
                                        title="Add table"
                                      >
                                        <Plus className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      <div className="flex items-center justify-between mt-3">
                        <Label className="text-xs">Bold names</Label>
                        <input
                          type="checkbox"
                          checked={selectedElement.guestListFontWeight === 'bold'}
                          onChange={(e) =>
                            updateElement(selectedElement.id, { guestListFontWeight: e.target.checked ? 'bold' : 'normal' })
                          }
                          className="rounded"
                        />
                      </div>

                      {selectedElement.type === 'guestListSurname' && (
                        <div className="mt-3">
                          <Label className="text-xs">Columns</Label>
                          <Input
                            type="number"
                            min={1}
                            className="h-8 mt-1"
                            value={selectedElement.guestListSurnameColumnCount ?? 1}
                            onChange={(e) => {
                              const raw = e.target.value.trim();
                              if (raw === '') {
                                updateElement(selectedElement.id, { guestListSurnameColumnCount: 1 });
                                return;
                              }
                              const n = parseInt(raw, 10);
                              if (!Number.isFinite(n) || n < 1) {
                                return;
                              }
                              updateElement(selectedElement.id, { guestListSurnameColumnCount: n });
                            }}
                          />
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Maximum number of vertical strips. Each strip is filled top to bottom until the box
                            height is used, then the next surname groups continue in the strip to the right.
                          </p>
                        </div>
                      )}

                      {selectedElement.type === 'guestList' && (
                      <div className="flex items-center justify-between mt-3">
                        <Label className="text-xs">Show Dividers</Label>
                        <input
                          type="checkbox"
                          checked={selectedElement.guestListShowDividers ?? true}
                          onChange={(e) => updateElement(selectedElement.id, { guestListShowDividers: e.target.checked })}
                          className="rounded"
                        />
                      </div>
                      )}
                    </>
                  )}

                        {selectedElement.type === 'shape' && (
                          <>
                      <div>
                              <Label className="text-xs">Shape Type</Label>
                        <Select 
                                value={selectedElement.shapeType || 'rectangle'}
                                onValueChange={(value) => updateElement(selectedElement.id, { shapeType: value as any })}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                                  <SelectItem value="rectangle">Rectangle</SelectItem>
                                  <SelectItem value="circle">Circle</SelectItem>
                                  <SelectItem value="line">Line</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                            <div className="grid grid-cols-2 gap-2">
                      <div>
                                <Label className="text-xs">Fill Color</Label>
                                <Input
                                  type="color"
                                  value={selectedElement.fillColor || '#3b82f6'}
                                  onChange={(e) => updateElement(selectedElement.id, { fillColor: e.target.value })}
                                  className="h-8"
                                />
                          </div>
                              <div>
                                <Label className="text-xs">Stroke</Label>
                                <Input
                                  type="color"
                                  value={selectedElement.strokeColor || '#1e3a8a'}
                                  onChange={(e) => updateElement(selectedElement.id, { strokeColor: e.target.value })}
                                  className="h-8"
                                />
                          </div>
                        </div>

                            <div>
                              <Label className="text-xs">Stroke Width</Label>
                              <Input
                                type="number"
                                value={selectedElement.strokeWidth || 2}
                                onChange={(e) => updateElement(selectedElement.id, { strokeWidth: parseInt(e.target.value) || 2 })}
                                className="h-8"
                              />
                      </div>
                    </>
                  )}

                        <div className="flex gap-2 pt-2 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => updateElement(selectedElement.id, { locked: !selectedElement.locked })}
                          >
                            {selectedElement.locked ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => duplicateElements()}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                            className="flex-1"
                            onClick={() => deleteSelectedElements()}
                  >
                            <Trash2 className="h-3 w-3" />
                  </Button>
                        </div>
                </CardContent>
              </Card>
                  </>
                ) : (
                  <div className="p-8 text-center text-gray-500 text-sm">
                    Select an element to edit its properties
                  </div>
                )}
              </TabsContent>

              <TabsContent value="layers" className="p-4 space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold">Layers</Label>
                  <span className="text-xs text-gray-500">{elements.length} elements</span>
          </div>
                {elements.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 text-sm">
                    No elements yet. Add some from the Add tab.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {[...elements].reverse().map((element, index) => {
                      const actualIndex = elements.length - 1 - index;
                      const isSelected = selectedElementIds.includes(element.id);
                      
                      return (
                        <div
                          key={element.id}
                          className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                            isSelected ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                          onClick={() => setSelectedElementIds([element.id])}
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateElement(element.id, { visible: !element.visible });
                            }}
                          >
                            {element.visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                          </Button>

                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{element.name}</div>
                            <div className="text-xs text-gray-500">{element.type}</div>
                          </div>

                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (actualIndex < elements.length - 1) {
                                  const newElements = [...elements];
                                  [newElements[actualIndex], newElements[actualIndex + 1]] = 
                                    [newElements[actualIndex + 1], newElements[actualIndex]];
                                  setElements(newElements);
                                  saveToHistory(newElements);
                                }
                              }}
                              disabled={actualIndex === elements.length - 1}
                            >
                              <ChevronUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (actualIndex > 0) {
                                  const newElements = [...elements];
                                  [newElements[actualIndex], newElements[actualIndex - 1]] = 
                                    [newElements[actualIndex - 1], newElements[actualIndex]];
                                  setElements(newElements);
                                  saveToHistory(newElements);
                                }
                              }}
                              disabled={actualIndex === 0}
                            >
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                duplicateElements([element.id]);
                              }}
                              title="Duplicate layer"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteSelectedElements([element.id]);
                              }}
                              title="Delete layer"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Canvas Area */}
          <div className="flex-1 flex flex-col">
            {/* Toolbar */}
            <div className="border-b p-2 bg-white flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1 border-r pr-3">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => alignElements('left')}
                  disabled={selectedElementIds.length === 0}
                  title="Align Left"
                >
                  <AlignLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => alignElements('center')}
                  disabled={selectedElementIds.length === 0}
                  title="Align Center"
                >
                  <AlignCenter className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => alignElements('right')}
                  disabled={selectedElementIds.length === 0}
                  title="Align Right"
                >
                  <AlignRight className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center gap-1 border-r pr-3">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => alignElements('top')}
                  disabled={selectedElementIds.length === 0}
                  title="Align Top"
                >
                  <AlignVerticalJustifyStart className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => alignElements('middle')}
                  disabled={selectedElementIds.length === 0}
                  title="Align Middle"
                >
                  <AlignVerticalJustifyCenter className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => alignElements('bottom')}
                  disabled={selectedElementIds.length === 0}
                  title="Align Bottom"
                >
                  <AlignVerticalJustifyEnd className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center gap-1 border-r pr-3">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => bringToFront(selectedElementIds)}
                  disabled={selectedElementIds.length === 0}
                  title="Bring to Front"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => sendToBack(selectedElementIds)}
                  disabled={selectedElementIds.length === 0}
                  title="Send to Back"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center gap-1 border-r pr-3">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => rotateSelectedElements(-15)}
                  disabled={selectedElementIds.length === 0}
                  title="Rotate -15°"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => rotateSelectedElements(15)}
                  disabled={selectedElementIds.length === 0}
                  title="Rotate +15°"
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={resetImageAspectRatio}
                  disabled={!hasSelectedImage}
                  title="Reset Aspect Ratio"
                >
                  <Ratio className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center gap-1 border-r pr-3">
                <Button
                  variant={showGrid ? 'default' : 'outline'}
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setShowGrid((prev) => !prev)}
                  title={showGrid ? 'Hide Grid' : 'Show Grid'}
                  aria-pressed={showGrid}
                >
                  <Grid3x3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={snapToGrid ? 'default' : 'outline'}
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setSnapToGrid((prev) => !prev)}
                  title={snapToGrid ? 'Disable Snap to Grid' : 'Enable Snap to Grid'}
                  aria-pressed={snapToGrid}
                >
                  <Move className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center gap-1 border-r pr-3">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={lockSelectedElements}
                  disabled={selectedElementIds.length === 0 || allSelectedLocked}
                  title="Lock Selection"
                >
                  <Lock className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={unlockSelectedElements}
                  disabled={selectedElementIds.length === 0 || !anySelectedLocked}
                  title="Unlock Selection"
                >
                  <Unlock className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => copyElements()}
                  disabled={selectedElementIds.length === 0}
                  title="Copy (Ctrl+C)"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => pasteElements()}
                  title="Paste (Ctrl+V)"
                >
                  <Download className="h-4 w-4 rotate-180" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => duplicateElements()}
                  disabled={selectedElementIds.length === 0}
                  title="Duplicate (Ctrl+D)"
                >
                  <div className="relative flex items-center justify-center">
                    <Copy className="h-4 w-4" />
                    <Plus className="absolute -bottom-1 -right-1 h-3 w-3" />
                  </div>
                </Button>
                <Button
                  variant="destructive"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => deleteSelectedElements()}
                  disabled={selectedElementIds.length === 0}
                  title="Delete (Del)"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Preview Canvas */}
            <div 
              ref={previewContainerRef} 
              className="flex-1 overflow-auto bg-gray-200 p-2"
              onWheel={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-center min-h-full">
                <div
                  className={`relative bg-white ${isExporting ? '' : 'shadow-2xl'}`}
                style={{
                  width: `${previewDimensions.width}px`,
                  height: `${previewDimensions.height}px`,
                }}
              >
                {/* Hide the brief zoom/pan reset that happens while capturing so the
                    canvas doesn't appear to jump on screen during export. */}
                {isExporting && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
                    <div className="flex flex-col items-center gap-2 text-gray-500">
                      <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                      <span className="text-sm">Generating PDF...</span>
                    </div>
                  </div>
                )}
                <Stage
                  ref={previewStageRef}
                  width={previewDimensions.width}
                  height={previewDimensions.height}
                  onClick={handleStageClick}
                  onTap={handleStageClick}
                  onContextMenu={handleStageContextMenu}
                  onWheel={handleStageWheel}
                  onMouseDown={handleStageMouseDown}
                  onMouseMove={handleStageMouseMove}
                  onMouseUp={handleStageMouseUp}
                  draggable={false}
                >
                  {/* Page layer - zoomed/panned with the design so elements stay aligned to the page */}
                  <Layer
                    scaleX={isExporting ? 1 : zoom}
                    scaleY={isExporting ? 1 : zoom}
                    x={isExporting ? 0 : panX}
                    y={isExporting ? 0 : panY}
                    listening={false}
                  >
                      {/* Canvas background — no stroke so export has no page-edge line */}
                      <Rect
                        x={0}
                        y={0}
                        width={previewDimensions.width}
                        height={previewDimensions.height}
                        fill="transparent"
                        fillEnabled={true}
                        strokeEnabled={false}
                        listening={false}
                      />
                      
                      {/* Canvas bounds indicator — hidden during export so it never appears in the PDF */}
                      {!isExporting && (
                        <Rect
                          x={0}
                          y={0}
                          width={previewDimensions.width}
                          height={previewDimensions.height}
                          fillEnabled={false}
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dash={[5, 5]}
                          listening={false}
                          draggable={false}
                          hitStrokeWidth={0}
                        />
                      )}

                      {!isExporting && renderGrid()}
                    {renderFrame()}
                  </Layer>
                  
                  {/* Interactive layer - affected by zoom/pan; export at 1:1 so no sub-pixel transform */}
                  <Layer
                    scaleX={isExporting ? 1 : zoom}
                    scaleY={isExporting ? 1 : zoom}
                    x={isExporting ? 0 : panX}
                    y={isExporting ? 0 : panY}
                  >
                    {elements.map((element) => renderElement(element))}
                      
                      {!isExporting && (
                        <Transformer
                          ref={transformerRef}
                          keepRatio={false}
                          enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right', 'top-center', 'middle-left', 'middle-right', 'bottom-center']}
                          boundBoxFunc={(oldBox, newBox) => {
                            // Prevent element from becoming too small
                            if (newBox.width < 10 || newBox.height < 10) {
                              return oldBox;
                            }
                            return newBox;
                          }}
                        />
                      )}
                  </Layer>
                </Stage>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedElementIds.length > 0 && (
              <span>{selectedElementIds.length} element{selectedElementIds.length > 1 ? 's' : ''} selected</span>
            )}
          </div>
          <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => void handleClose()}>
            Close
          </Button>
            <Button 
              onClick={handleExport} 
              className="bg-blue-600 hover:bg-blue-700"
              data-export-button
            >
              <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>
      </div>

      {showCreateDesignModal && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4 shadow-xl">
            <h2 className="text-xl font-bold mb-4">New design plan</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Design name</label>
                <Input
                  value={newDesignName}
                  onChange={(e) => setNewDesignName(e.target.value)}
                  placeholder="e.g. Main hall, Evening layout…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      void createFloorPlanDesign();
                    }
                  }}
                  autoFocus
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateDesignModal(false);
                    setNewDesignName('');
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={() => void createFloorPlanDesign()} disabled={!newDesignName.trim()}>
                  Create floor plan
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {renameDesignTarget && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4 shadow-xl">
            <h2 className="text-xl font-bold mb-4">Rename floor plan</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Design name</label>
                <Input
                  value={renameDesignTarget.name}
                  onChange={(e) =>
                    setRenameDesignTarget((prev) =>
                      prev ? { ...prev, name: e.target.value } : prev
                    )
                  }
                  placeholder="Design name"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      void renameFloorPlanDesign();
                    }
                  }}
                  autoFocus
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setRenameDesignTarget(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => void renameFloorPlanDesign()}
                  disabled={!renameDesignTarget.name.trim()}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Right-click Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[100] min-w-[200px]"
          style={{
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase">
            Select Layer
          </div>
          {contextMenu.elements.map((element, index) => (
            <button
              key={element.id}
              className="w-full text-left px-4 py-2 hover:bg-blue-50 flex items-center justify-between group transition-colors"
              onClick={() => {
                setSelectedElementIds([element.id]);
                setContextMenu(null);
              }}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded group-hover:bg-blue-100 transition-colors">
                  {element.type === 'text' && <Type className="h-3 w-3 text-gray-600 group-hover:text-blue-600" />}
                  {element.type === 'image' && <ImageIcon className="h-3 w-3 text-gray-600 group-hover:text-blue-600" />}
                  {element.type === 'unitLogo' && <Building2 className="h-3 w-3 text-gray-600 group-hover:text-blue-600" />}
                  {element.type === 'tablePlan' && <LayoutGrid className="h-3 w-3 text-gray-600 group-hover:text-blue-600" />}
                  {(element.type === 'guestList' || element.type === 'guestListSurname') && (
                    <Users className="h-3 w-3 text-gray-600 group-hover:text-blue-600" />
                  )}
                  {element.type === 'shape' && <LayoutGrid className="h-3 w-3 text-gray-600 group-hover:text-blue-600" />}
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">{element.name}</div>
                  <div className="text-xs text-gray-500">{getElementTypeLabel(element.type)}</div>
                </div>
              </div>
              <div className="text-xs text-gray-400">
                Layer {contextMenu.elements.length - index}
              </div>
            </button>
          ))}
          <div className="px-3 py-2 border-t border-gray-100 text-xs text-gray-500">
            Right-click to select from {contextMenu.elements.length} overlapping layers
          </div>
        </div>
      )}
    </div>
  );
}
